import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import api, { documentAPI, Document, DocumentQA, notificationSettingsAPI } from '../services/api';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../hooks/useLanguage';
import { SectionHeader, SummaryCard } from '../components/ui';
import UpgradeModal from '../components/UpgradeModal';
import { UPGRADE_MESSAGE_KEYS } from '../constants/subscription';
import {
	ClipboardIcon, AlertIcon, ChatIcon, ScaleIcon,
	FileTextIcon, CalendarIcon, TrashIcon, ChevronDownIcon,
	ChevronUpIcon, ChevronRightIcon, LightbulbIcon, DollarSignIcon,
	HomeIcon, UserIcon, CreditCardIcon, PawPrintIcon,
	PaintbrushIcon, WrenchIcon, SearchIcon, ShieldIcon,
		BuildingIcon, RefreshCwIcon, MailIcon, ArrowRightIcon,
	  ZapIcon, TrendingUpIcon,
  DownloadIcon, ShareIcon, EditIcon, CheckIcon, CloseIcon,
  MoreIcon, CheckCircleIcon, LoaderIcon, AlertCircleIcon, StarIcon, ClockIcon, LockIcon
} from '../components/Icons';
import { RISK_DISCLAIMER } from '../constants/sections';
import { NotificationSettings } from '../components/NotificationSettings';
import ContractProcessingCard from '../components/ContractProcessingCard';
import i18n from '../i18n';
import './DocumentDetailNew.css';
import './Landing.css';

type StrippedTemplate = {
  text: string;
  vars: string[];
  ranges: Array<{ start: number; end: number }>;
};


const getRandomMotivation = (t?: any) => {
  try {
    const list = t ? (t('documents:processing.motivations', { returnObjects: true }) as unknown as string[]) : [];
    if (Array.isArray(list) && list.length > 0) {
      const idx = Math.floor(Math.random() * list.length);
      return list[idx];
    }
  } catch (e) {
    return '';
  }
  return t ? (t('documents:processing.motivations.0', { defaultValue: '' }) as string) : '';
};

const stripTemplateBraces = (suggestion: string): StrippedTemplate => {
  const vars: string[] = [];
  const ranges: Array<{ start: number; end: number }> = [];
  let output = '';
  let lastIndex = 0;
  let outputIndex = 0;

  const matches = Array.from(suggestion.matchAll(/\{([^}]+)\}/g));
  matches.forEach((match) => {
    const matchIndex = match.index ?? 0;
    const before = suggestion.slice(lastIndex, matchIndex);
    output += before;
    outputIndex += before.length;

    const varName = match[1] ?? '';
    vars.push(varName);
    ranges.push({ start: outputIndex, end: outputIndex + varName.length });
    output += varName;
    outputIndex += varName.length;

    lastIndex = matchIndex + match[0].length;
  });

  if (lastIndex < suggestion.length) {
    output += suggestion.slice(lastIndex);
  }

  return { text: output, vars, ranges };
};

const normalizePlaceholderBraces = (value: string) => {
  if (!value) return value;
  // Some AI outputs may include double braces like `{{N}}`; normalize to `{N}` for consistent UI parsing/highlighting.
  return value.replace(/\{\{([^{}]+)\}\}/g, '{$1}');
};

const extractPlaceholders = (value: string) => {
  const placeholders = new Set<string>();
  if (!value) return placeholders;

  const normalized = normalizePlaceholderBraces(value);
  const regex = /\{([^{}]+)\}/g;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(normalized)) !== null) {
    const name = (match[1] ?? '').trim();
    if (!name) continue;
    placeholders.add(name);
  }
  return placeholders;
};

const renderSuggestedQuestionLabel = (suggestion: string) => {
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  const matches = Array.from(suggestion.matchAll(/\{([^}]+)\}/g));

  matches.forEach((match, i) => {
    const matchIndex = match.index ?? 0;
    if (matchIndex > lastIndex) {
      parts.push(<span key={`t-${i}-${lastIndex}`}>{suggestion.slice(lastIndex, matchIndex)}</span>);
    }

    const varName = match[1] ?? '';
    parts.push(
      <span key={`p-${i}-${matchIndex}`} className="qa-template-var">
        {varName}
      </span>
    );

    lastIndex = matchIndex + match[0].length;
  });

  if (lastIndex < suggestion.length) {
    parts.push(<span key={`t-end-${lastIndex}`}>{suggestion.slice(lastIndex)}</span>);
  }

  return parts.length ? parts : suggestion;
};

const EXCERPT_LABELS = [
  'trecho do contrato',
  'trecho de contrato',
  'trecho importante',
  'contract excerpt',
  'excerpt from contract',
  'important excerpt',
];

const stripQuotes = (value: string) => value.replace(/^[\s"“”']+|[\s"“”']+$/g, '').trim();

const extractAnswerExcerpt = (answer: string) => {
  if (!answer) return { body: answer, excerpt: null };
  const lower = answer.toLowerCase();
  let matchIndex = -1;
  let matchedLabel = '';

  for (const label of EXCERPT_LABELS) {
    const idx = lower.indexOf(label);
    if (idx !== -1 && (matchIndex === -1 || idx < matchIndex)) {
      matchIndex = idx;
      matchedLabel = label;
    }
  }

  if (matchIndex === -1) {
    return { body: answer, excerpt: null };
  }

  // Find the start of the label respecting line boundaries
  const labelRegex = new RegExp(`(?:^|\\n)\\s*${matchedLabel.replace(/[-/\\\\^$*+?.()|[\\]{}]/g, '\\$&')}\\s*:`, 'i');
  const labelMatch = answer.match(labelRegex);
  if (!labelMatch || typeof labelMatch.index !== 'number') {
    return { body: answer, excerpt: null };
  }

  const prefix = answer.slice(0, labelMatch.index).trim();
  const tail = answer.slice(labelMatch.index + labelMatch[0].length).trim();
  const paragraphs = tail.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);

  let excerpt: string | null = null;
  let remainingParas = paragraphs;
  if (paragraphs.length > 0) {
    const first = paragraphs[0];
    const startsWithQuote = /^["“”']/.test(first);
    const endsWithQuote = /["“”']$/.test(first);
    if (startsWithQuote || endsWithQuote) {
      excerpt = first;
      remainingParas = paragraphs.slice(1);
    }
  }

  const bodyParts = [prefix, remainingParas.join('\n\n')].map((part) => part.trim()).filter(Boolean);
  const body = bodyParts.join('\n\n') || answer;

  return {
    body,
    excerpt,
  };
};

const splitExcerptItems = (excerpt: string | null | undefined) => {
  if (!excerpt) return [];
  return excerpt
    .split(/\n+/)
    .flatMap((part) => part.split(/"\s*"/))
    .map(stripQuotes)
    .filter(Boolean);
};

const shouldShowTemplateHint = (question: string, vars: string[]) => {
  return vars.some((v) => {
    if (!v) return false;
    if (/^[A-Z]$/.test(v)) {
      return new RegExp(`\\b${v}\\b`).test(question);
    }
    return question.includes(v);
  });
};

const createFormatValue = (t: (key: string) => string) => {
  const notSpecified = t('detail.values.notSpecified');
  const yes = t('detail.values.yes');
  const no = t('detail.values.no');

  const formatValue = (value: any): string => {
    // Handle null/undefined
    if (value === null || value === undefined) {
      return notSpecified;
    }

    // Convert to string first for easier checking
    const strValue = String(value).trim();

    // Handle common "not found" AI responses
    const notFoundPatterns = [
      'N/A',
      'n/a',
      'NA',
      'não encontrado',
      'não identificado',
      'não especificado',
      'não consta',
      'não informado',
      'não disponível',
      'not found',
      'not specified',
      'not available',
      'not identified'
    ];

    if (notFoundPatterns.some(pattern =>
      strValue.toLowerCase() === pattern.toLowerCase()
    )) {
      return notSpecified;
    }

    // Handle boolean
    if (typeof value === 'boolean') {
      return value ? yes : no;
    }

    // Handle objects (but not arrays)
    if (typeof value === 'object' && !Array.isArray(value)) {
      // Try to create a readable string from object properties
      const entries = Object.entries(value)
        .filter(([, v]) => {
          const formatted = formatValue(v);
          return formatted && formatted !== notSpecified;
        })
        .map(([k, v]) => `${k}: ${formatValue(v)}`);
      return entries.join(', ') || notSpecified;
    }

    // Handle arrays
    if (Array.isArray(value)) {
      const formatted = value
        .map(v => formatValue(v))
        .filter(v => v && v !== notSpecified);
      return formatted.join(', ') || notSpecified;
    }

    // Default: return the string value
    return strValue;
  };

  return formatValue;
};

const TruncatedText: React.FC<{ text: string; maxLength?: number; expandLabel?: string; collapseLabel?: string }> = ({
  text,
  maxLength = 100,
  expandLabel = 'ver mais',
  collapseLabel = 'ver menos'
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (text.length <= maxLength) {
    return <span>{text}</span>;
  }

  return (
    <span>
      {isExpanded ? text : `${text.substring(0, maxLength)}...`}
      {' '}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="text-expand-btn"
        type="button"
      >
        {isExpanded ? collapseLabel : expandLabel}
      </button>
    </span>
  );
};

interface DocumentDetailNewProps {
  prefetchedDocument?: Document | null;
  readOnly?: boolean;
}

const RATING_STORAGE_KEY = 'qa_ratings';

export const loadStoredRatings = (docId?: string | number | null): Record<number, number> => {
  if (typeof window === 'undefined' || !docId) return {};
  try {
    const raw = localStorage.getItem(RATING_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    const docRatings = parsed[String(docId)] || {};
    const result: Record<number, number> = {};
    Object.entries(docRatings).forEach(([qid, val]) => {
      const numQid = Number(qid);
      if (!Number.isNaN(numQid) && typeof val === 'number') {
        result[numQid] = val;
      }
    });
    return result;
  } catch (err) {
    console.error('Failed to load stored ratings', err);
    return {};
  }
};

export const saveStoredRating = (docId: string | number, questionId: number, rating: number) => {
  if (typeof window === 'undefined') return;
  try {
    const raw = localStorage.getItem(RATING_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    if (!parsed[String(docId)]) parsed[String(docId)] = {};
    parsed[String(docId)][String(questionId)] = rating;
    localStorage.setItem(RATING_STORAGE_KEY, JSON.stringify(parsed));
  } catch (err) {
    console.error('Failed to persist rating', err);
  }
};

const PROCESSING_STAGE_INTERVAL_MS = 8000;

type FloatingNavItem = {
  id: string;
  label: string;
  icon: React.ReactNode;
};

type FloatingNavTranslations = {
  sectionsLabel: string;
  hoverHint: string;
  documentNav: string;
  documentSections: string;
};

const FloatingSectionNav: React.FC<{ items: FloatingNavItem[]; translations: FloatingNavTranslations }> = ({ items, translations }) => {
  const rootRef = React.useRef<HTMLDivElement>(null);
  const [open, setOpen] = React.useState(false);
  const [activeId, setActiveId] = React.useState<string | null>(items[0]?.id ?? null);
  const [showHint, setShowHint] = React.useState(false);
  const hintSeenRef = React.useRef(false);
  const isHoveringRef = React.useRef(false);
  const pinnedRef = React.useRef(false);

  const markHintSeen = useCallback(() => {
    if (hintSeenRef.current) return;
    hintSeenRef.current = true;
    setShowHint(false);
    try {
      window.localStorage.setItem('docDetailFloatingNavHintSeen', '1');
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    try {
      const seen = window.localStorage.getItem('docDetailFloatingNavHintSeen') === '1';
      hintSeenRef.current = seen;
      if (!seen) setShowHint(true);
    } catch {
      setShowHint(true);
    }
  }, []);

  useEffect(() => {
    if (!showHint) return;
    const timer = window.setTimeout(() => {
      markHintSeen();
    }, 8000);
    return () => window.clearTimeout(timer);
  }, [showHint, markHintSeen]);

  useEffect(() => {
    if (items.length === 0) {
      setActiveId(null);
      return;
    }
    setActiveId((prev) => (prev && items.some((i) => i.id === prev) ? prev : items[0].id));
  }, [items]);

  useEffect(() => {
    if (items.length === 0) return;

    const elements = items
      .map((item) => window.document.getElementById(item.id))
      .filter(Boolean) as HTMLElement[];

    if (elements.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const best = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (best?.target?.id) setActiveId(best.target.id);
      },
      { root: null, rootMargin: '-20% 0px -65% 0px', threshold: [0.15, 0.25, 0.4] }
    );

    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [items]);

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      if (pinnedRef.current) return;
      if (isHoveringRef.current) return;
      const rect = rootRef.current?.getBoundingClientRect();
      if (!rect) return;

      const paddingX = 64;
      const paddingY = 180;
      const within =
        event.clientX >= rect.left - paddingX &&
        event.clientX <= rect.right + paddingX &&
        event.clientY >= rect.top - paddingY &&
        event.clientY <= rect.bottom + paddingY;

      if (within) {
        setOpen(true);
        markHintSeen();
      } else if (open) {
        setOpen(false);
      }
    };

    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [markHintSeen, open]);

  if (items.length < 2) return null;

  const activeItem = items.find((item) => item.id === activeId) || items[0];

  const wheelSize = 276;
  const centerX = 30;
  const centerY = wheelSize / 2;
  const radius = 122;
  const angleStart = -70;
  const angleEnd = 70;
  const step = items.length === 1 ? 0 : (angleEnd - angleStart) / (items.length - 1);

  return (
    <div
      ref={rootRef}
      className={`floating-nav ${open ? 'is-open' : ''} ${showHint ? 'has-hint' : ''}`}
      onMouseEnter={() => {
        isHoveringRef.current = true;
        setOpen(true);
        markHintSeen();
      }}
      onMouseLeave={() => {
        isHoveringRef.current = false;
        if (!pinnedRef.current) setOpen(false);
      }}
    >
      <button
        type="button"
        className="floating-nav-handle"
        aria-label={translations.documentNav}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => {
          markHintSeen();
          setOpen((prev) => {
            const next = !prev;
            pinnedRef.current = next;
            return next;
          });
        }}
        onFocus={() => {
          markHintSeen();
          setOpen(true);
        }}
        onBlur={() => {
          if (!pinnedRef.current) setOpen(false);
        }}
      >
        <span className="floating-nav-handle-icon" aria-hidden="true">
          {activeItem?.icon}
        </span>
        <span className="floating-nav-handle-text">{activeItem?.label || translations.sectionsLabel}</span>
      </button>

      {showHint && (
        <div className="floating-nav-hint" role="note">
          {translations.hoverHint}
        </div>
      )}

      <div
        className="floating-nav-wheel"
        style={{ width: wheelSize, height: wheelSize }}
        role="navigation"
        aria-label={translations.documentSections}
      >
        {items.map((item, index) => {
          const deg = angleStart + index * step;
          const rad = (deg * Math.PI) / 180;
          const x = centerX + Math.cos(rad) * radius;
          const y = centerY + Math.sin(rad) * radius;

          return (
            <button
              key={item.id}
              type="button"
              className={`floating-nav-item ${activeId === item.id ? 'is-active' : ''}`}
              style={{ left: x, top: y }}
              data-label={item.label}
              aria-label={item.label}
              onClick={() => {
                window.document.getElementById(item.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
            >
              {item.icon}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const createProcessingStages = (t: (key: string) => string) => [
  { title: t('detail.processing.steps.preparing.title'), subtitle: t('detail.processing.steps.preparing.subtitle'), icon: <RefreshCwIcon size={18} className="spin" /> },
  { title: t('detail.processing.steps.extracting.title'), subtitle: t('detail.processing.steps.extracting.subtitle'), icon: <FileTextIcon size={18} /> },
  { title: t('detail.processing.steps.identifying.title'), subtitle: t('detail.processing.steps.identifying.subtitle'), icon: <SearchIcon size={18} /> },
  { title: t('detail.processing.steps.context.title'), subtitle: t('detail.processing.steps.context.subtitle'), icon: <ScaleIcon size={18} /> },
  { title: t('detail.processing.steps.risks.title'), subtitle: t('detail.processing.steps.risks.subtitle'), icon: <ShieldIcon size={18} /> },
  { title: t('detail.processing.steps.recommendations.title'), subtitle: t('detail.processing.steps.recommendations.subtitle'), icon: <LightbulbIcon size={18} /> },
  { title: t('detail.processing.steps.report.title'), subtitle: t('detail.processing.steps.report.subtitle'), icon: <ZapIcon size={18} /> },
  { title: t('detail.processing.steps.finalizing.title'), subtitle: t('detail.processing.steps.finalizing.subtitle'), icon: <CheckIcon size={18} /> },
];

const DocumentDetailNew: React.FC<DocumentDetailNewProps> = ({ prefetchedDocument, readOnly = false }) => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const { t, i18n } = useTranslation(['documents', 'common']);
  const { getLocalizedPath } = useLanguage();
  const { hasRiskDetails, hasMessageBuilder, limits, isPremiumTier, isPlatinumTier } = useSubscription();

  // Create translated functions
  const formatValue = createFormatValue(t);
  const processingStages = createProcessingStages(t);
  const formatPartyFieldLabel = createFormatPartyFieldLabel(t);
  const getPartyCardMeta = createGetPartyCardMeta(t, formatPartyFieldLabel);
  const buildPartyCards = createBuildPartyCards(t, formatPartyFieldLabel, getPartyCardMeta, formatValue);
  const notSpecified = t('detail.values.notSpecified');
  const renderSummaryCards = createRenderSummaryCards(formatValue, notSpecified);
  const riskAccordionTranslations: RiskAccordionTranslations = {
    severityHigh: t('detail.risks.card.severity.high'),
    severityMedium: t('detail.risks.card.severity.medium'),
    severityLow: t('detail.risks.card.severity.low'),
    upgradeAriaLabel: t('detail.risks.locked.upgradeAriaLabel'),
    unlockTitle: t('detail.risks.locked.title'),
    unlockDescription: t('detail.risks.locked.description'),
    showLegalBasis: t('detail.risks.card.showLegalBasis'),
    hideLegalBasis: t('detail.risks.card.hideLegalBasis'),
    whatYouCanDo: t('detail.risks.card.whatYouCanDo'),
    contractClause: t('detail.risks.card.contractClause'),
    whyItMatters: t('detail.risks.card.whyItMatters'),
    suggestedText: t('detail.risks.card.suggestedText'),
    legalBasis: t('detail.risks.card.legalBasis'),
    includeInMessage: t('detail.risks.card.includeInMessage'),
    financialImpactTag: t('detail.risks.card.financialImpactTag'),
    defaultImpactSummary: t('detail.risks.card.defaultImpactSummary'),
    whenItHappens: t('detail.risks.card.whenItHappens'),
    negotiationLevel: t('detail.risks.card.negotiationLevel'),
    negotiationLevels: t('detail.risks.card.negotiationLevels', { returnObjects: true }) as unknown as { alta: string; media: string; baixa: string },
    actionItems: t('detail.risks.card.actionItems'),
    deepAnalysis: t('detail.risks.card.deepAnalysis'),
    deepLoading: t('detail.risks.card.deepLoading'),
    deepMarketComparison: t('detail.risks.card.deepMarketComparison'),
    deepNegotiationStrength: t('detail.risks.card.deepNegotiationStrength'),
    deepWorstCase: t('detail.risks.card.deepWorstCase'),
    marketStatus: t('detail.risks.card.marketStatus', { returnObjects: true }) as unknown as { mais_rigida: string; padrao: string; favoravel: string },
    marketBaseline: t('detail.risks.card.marketBaseline'),
    thisContract: t('detail.risks.card.thisContract'),
    marketDeltaHigher: t('detail.risks.card.marketDeltaHigher'),
    marketDeltaLower: t('detail.risks.card.marketDeltaLower'),
    marketDeltaEqual: t('detail.risks.card.marketDeltaEqual'),
    worstCaseExampleLabel: t('detail.risks.card.worstCaseExampleLabel'),
    addToNegotiation: t('detail.risks.card.addToNegotiation'),
    addedToNegotiation: t('detail.risks.card.addedToNegotiation'),
    deepTeaser: t('detail.risks.card.deepTeaser'),
    deepClose: t('detail.risks.card.deepClose'),
    deepOpen: t('detail.risks.card.deepOpen'),
    deepFullLabel: t('detail.risks.card.deepFullLabel'),
    predictedOutcome: t('detail.risks.card.predictedOutcome'),
    loadingMarket: t('detail.risks.card.loadingMarket'),
    loadingNegotiation: t('detail.risks.card.loadingNegotiation'),
    loadingWorst: t('detail.risks.card.loadingWorst'),
    confidence: t('detail.risks.card.confidence'),
    negotiationStrengthAria: t('detail.risks.card.negotiationStrengthAria'),
    negotiationStrengthSublabel: t('detail.risks.card.negotiationStrengthSublabel'),
    legalFundamentos: t('detail.risks.card.legalFundamentos'),
    clauseCount: t('detail.risks.card.clauseCount'),
    legalBasisMeta: t('detail.risks.card.legalBasisMeta'),
    showScenarios: t('detail.risks.card.showScenarios'),
    hideScenarios: t('detail.risks.card.hideScenarios'),
    sliderNavAria: t('detail.risks.slider.navAria'),
    sliderPrevAria: t('detail.risks.slider.prevAria'),
    sliderNextAria: t('detail.risks.slider.nextAria'),
    sliderPrev: t('detail.risks.slider.prev'),
    sliderNext: t('detail.risks.slider.next'),
    understood: t('detail.risks.card.understood'),
    counter: t('detail.risks.card.counter'),
  };
  const hasNotifications = isPremiumTier || isPlatinumTier;
  const navDocAppliedRef = useRef(false);
  const [document, setDocument] = useState<Document | null>(null);
  const [loading, setLoading] = useState(true);
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [askingQuestion, setAskingQuestion] = useState(false);
  const [ratingValue, setRatingValue] = useState<number | null>(null);
  const [ratingSubmitting, setRatingSubmitting] = useState(false);
  const [ratingMessage, setRatingMessage] = useState<string>('');
  const [ratingError, setRatingError] = useState<string>('');

  const [qaHistory, setQaHistory] = useState<DocumentQA[]>([]);
  const [ratings, setRatings] = useState<Record<number, number>>({});
  const [currentAnswerId, setCurrentAnswerId] = useState<number | null>(null);
  const [deletingQuestionId, setDeletingQuestionId] = useState<number | null>(null);
  const [questionsUsedThisMonth, setQuestionsUsedThisMonth] = useState<number>(0);
  const lastAskedQuestionRef = useRef<string>('');
  const questionInputRef = useRef<HTMLTextAreaElement>(null);
  const [activeTemplateVars, setActiveTemplateVars] = useState<string[]>([]);
  const [shareOpen, setShareOpen] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [shareError, setShareError] = useState('');
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const [shareNotice, setShareNotice] = useState('');
  const [shareLink, setShareLink] = useState('');
  const [selectedMotivation] = useState<string>(() => {
    const candidate = getRandomMotivation(t);
    if (candidate && candidate.trim().length > 0) return candidate;
    return t('documents:processing.motivations.0', { defaultValue: '' }) as string;
  });
  const shareMenuRef = useRef<HTMLDivElement>(null);
  const moreMenuRef = useRef<HTMLDivElement>(null);
  const [showNotificationsModal, setShowNotificationsModal] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [nameError, setNameError] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeMessage, setUpgradeMessage] = useState('');
  const [processingStage, setProcessingStage] = useState(0);
  const [processingError, setProcessingError] = useState<string | null>(null);
  const [summaryExpanded, setSummaryExpanded] = useState(false);
  // Message Builder state
  const [selectedRisks, setSelectedRisks] = useState<Set<number>>(new Set());
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [generatedMessage, setGeneratedMessage] = useState('');
  const [generatingMessage, setGeneratingMessage] = useState(false);
  const [messageCopied, setMessageCopied] = useState(false);
  const [messageCopyWarning, setMessageCopyWarning] = useState('');
  const [excerptOpenKeys, setExcerptOpenKeys] = useState<Set<string>>(new Set());
  const [selectedFilter, setSelectedFilter] = useState<'high' | 'medium' | 'low' | null>(null);
  const isProcessing = document?.status === 'processing';
  const questionsLimitPerContractMonth = limits?.questions_per_contract_month || 0;
  const hasQaAccess = readOnly || questionsLimitPerContractMonth > 0;
  const postCompletePollTimerRef = useRef<NodeJS.Timeout | null>(null);
  const postCompletePollAttemptsRef = useRef(0);

  const loadDocument = useCallback(async () => {
    // If we already have a prefetched document (anonymous preview), use it directly
    if (prefetchedDocument) {
      setDocument(prefetchedDocument);
      setQaHistory(prefetchedDocument.qa_history || []);
      setLoading(false);
      return;
    }

    // If navigation provided a lightweight document payload (e.g., after upload), use it immediately (only once)
    const navDoc = (location.state as any)?.document as Document | undefined;
    if (navDoc && !navDocAppliedRef.current) {
      setDocument(navDoc);
      setQaHistory(navDoc.qa_history || []);
      const storedRatings = loadStoredRatings(id);
      const mergedRatings = (navDoc.qa_history || []).reduce((acc, qa: any) => {
        if (typeof qa.rating === 'number') acc[qa.id] = qa.rating;
        return acc;
      }, { ...storedRatings } as Record<number, number>);
      setRatings(mergedRatings);
      setLoading(false);
      navDocAppliedRef.current = true;
    }

    if (!id) return;
    try {
      const docRes = await documentAPI.get(id);
      setDocument(docRes.data);
      setQaHistory(docRes.data.qa_history || []);
      const structuredAnalysis = (docRes.data as any)?.analysis?.structured_analysis || (docRes.data as any)?.analysis;
      const failureMessage =
        structuredAnalysis?.error ||
        structuredAnalysis?.message ||
        structuredAnalysis?.status_message ||
        structuredAnalysis?.statusText;
      if (docRes.data.status === 'failed') {
        setProcessingError(failureMessage || t('documents:detail.processingError.message'));
      } else {
        setProcessingError(null);
      }
      const storedRatings = loadStoredRatings(id);
      const mergedRatings = (docRes.data.qa_history || []).reduce((acc, qa: any) => {
        if (typeof qa.rating === 'number') acc[qa.id] = qa.rating;
        return acc;
      }, { ...storedRatings } as Record<number, number>);
      setRatings(mergedRatings);

      // Fetch the actual usage counter from the backend
      try {
        const usageRes = await api.get(`/documents/${id}/usage`);
        setQuestionsUsedThisMonth(usageRes.data.questions_used || 0);
      } catch (usageErr) {
        console.error('Failed to fetch question usage:', usageErr);
        // Fallback to counting qaHistory if API fails
        const now = new Date();
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        const count = (docRes.data.qa_history || []).filter(qa => new Date(qa.created_at) >= monthStart).length;
        setQuestionsUsedThisMonth(count);
      }
    } catch (err) {
      console.error('Failed to load document:', err);
      const errMsg =
        (err as any)?.response?.data?.detail ||
        (err as any)?.message ||
        t('documents:detail.processingError.message');
      setProcessingError(typeof errMsg === 'string' ? errMsg : t('documents:detail.processingError.message'));
    } finally {
      setLoading(false);
    }
  }, [id, prefetchedDocument, location.state, t]);

  // Refresh user data on mount to ensure we have the latest subscription tier
  useEffect(() => {
    refreshUser().catch(() => {
      // Silently fail if refresh fails - user data will be stale but app continues working
    });
  }, [refreshUser]);

  useEffect(() => {
    loadDocument();
  }, [loadDocument]);

  // Keep notificationsEnabled in sync when reopening the page
  useEffect(() => {
    const fetchNotificationStatus = async () => {
      if (!document || !hasNotifications) return;
      try {
        const res = await notificationSettingsAPI.getSettings(document.id);
        setNotificationsEnabled(res.data.notifications_enabled);
      } catch (err) {
        // Ignore if user has no access or request fails; button will fall back to default
        console.error('Failed to load notification settings:', err);
      }
    };
    fetchNotificationStatus();
  }, [document, hasNotifications]);

  useEffect(() => {
    // Update rating value when ratings change or answer changes
    setRatingValue(currentAnswerId ? ratings[currentAnswerId] ?? null : null);
  }, [currentAnswerId, ratings]);

  useEffect(() => {
    // Reset rating messages only when the displayed answer changes
    setRatingMessage('');
    setRatingError('');
  }, [currentAnswerId]);

  const toggleExcerpt = (key: string) => {
    setExcerptOpenKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const pollTimerRef = useRef<NodeJS.Timeout | null>(null);
  const pollAttemptsRef = useRef(0);

  useEffect(() => {
    if (!prefetchedDocument && document?.status === 'processing') {
      // reset attempts and clear any previous timer
      pollAttemptsRef.current = 0;
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
      }
      pollTimerRef.current = setInterval(() => {
        pollAttemptsRef.current += 1;
        loadDocument();
        if (pollAttemptsRef.current >= 60) {
          clearInterval(pollTimerRef.current as NodeJS.Timeout);
          pollTimerRef.current = null;
        }
      }, 3000);
      return () => {
        if (pollTimerRef.current) {
          clearInterval(pollTimerRef.current);
          pollTimerRef.current = null;
        }
      };
    }
    // If not processing, clear polling
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, [document?.status, loadDocument, prefetchedDocument]);

  useEffect(() => {
    return () => {
      if (postCompletePollTimerRef.current) {
        clearInterval(postCompletePollTimerRef.current);
        postCompletePollTimerRef.current = null;
      }
    };
  }, []);

  // Track when user views risks section
  const hasTrackedRisksView = useRef(false);
  useEffect(() => {
    if (readOnly || !document || !id) return;

    const analysisRaw: any = (document as any)?.analysis || {};
    const analysisObj: any =
      analysisRaw?.structured_analysis ||
      analysisRaw?.result ||
      analysisRaw?.full_analysis ||
      analysisRaw ||
      {};
    const risks = analysisObj?.risks || [];

    // Track if risks are available and we haven't tracked yet
    if (risks.length > 0 && !hasTrackedRisksView.current) {
      hasTrackedRisksView.current = true;
      api.post(`/metrics/feedback`, {
        document_id: Number(id),
        feedback_type: 'overall_analysis',
        rating: 5
      }).catch(err => console.error('Failed to track viewed_risks:', err));
    }
  }, [document, id, readOnly]);

  useEffect(() => {
    if (prefetchedDocument || !id) {
      if (postCompletePollTimerRef.current) {
        clearInterval(postCompletePollTimerRef.current);
        postCompletePollTimerRef.current = null;
      }
      return;
    }

    // If the document is already marked completed but summary/parties are still missing,
    // keep polling briefly (stage1 can finish after stage2 in parallel processing).
    const analysisRaw: any = (document as any)?.analysis || {};
    const analysisObj: any =
      analysisRaw?.structured_analysis ||
      analysisRaw?.result ||
      analysisRaw?.full_analysis ||
      analysisRaw ||
      {};
    const summaryObj: any = analysisObj?.summary || analysisObj?.full_summary || {};
    const hasSummary = Boolean(summaryObj) && typeof summaryObj === 'object' && Object.keys(summaryObj).length > 0;
    const partiesObj: any = analysisObj?.parties?.parties || analysisObj?.parties || {};
    const hasParties = buildPartyCards(partiesObj).length > 0;
    const needsEnrichment = document?.status === 'completed' && (!hasSummary || !hasParties);

    if (!needsEnrichment) {
      if (postCompletePollTimerRef.current) {
        clearInterval(postCompletePollTimerRef.current);
        postCompletePollTimerRef.current = null;
      }
      return;
    }

    if (postCompletePollTimerRef.current) {
      return;
    }

    postCompletePollAttemptsRef.current = 0;
    postCompletePollTimerRef.current = setInterval(() => {
      postCompletePollAttemptsRef.current += 1;
      loadDocument();
      if (postCompletePollAttemptsRef.current >= 10) {
        clearInterval(postCompletePollTimerRef.current as NodeJS.Timeout);
        postCompletePollTimerRef.current = null;
      }
    }, 2000);
  }, [document, id, loadDocument, prefetchedDocument, buildPartyCards]);

  // Scroll to top when navigating to a document page
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  useEffect(() => {
    if (!isProcessing) {
      setProcessingStage(0);
      return;
    }
    setProcessingStage(0);
    let current = 0;
    const interval = setInterval(() => {
      current += 1;
      setProcessingStage((prev) => {
        const next = Math.min(current, processingStages.length - 1);
        return next;
      });
      if (current >= processingStages.length - 1) {
        clearInterval(interval);
      }
    }, PROCESSING_STAGE_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [isProcessing, document?.id, processingStages.length]);

  useEffect(() => {
    if (document && !isEditingName) {
      setNameDraft(document.auto_generated_name);
    }
  }, [document, isEditingName]);

  // Surface processing errors as soon as the backend marks the document as failed
  useEffect(() => {
    if (!document) return;
    if (document.status === 'failed') {
      const structured = (document as any)?.analysis?.structured_analysis || (document as any)?.analysis;
      const message =
        structured?.error ||
        structured?.message ||
        structured?.status_message ||
        structured?.statusText ||
        t('documents:detail.processingError.message');
      setProcessingError(message);
    } else {
      setProcessingError(null);
    }
  }, [document, t]);

  useEffect(() => {
    if (!shareOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (shareMenuRef.current && !shareMenuRef.current.contains(event.target as Node)) {
        setShareOpen(false);
      }
    };
    window.document.addEventListener('mousedown', handleClickOutside);
    return () => window.document.removeEventListener('mousedown', handleClickOutside);
  }, [shareOpen]);

  useEffect(() => {
    if (!moreMenuOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(event.target as Node)) {
        setMoreMenuOpen(false);
      }
    };
    window.document.addEventListener('mousedown', handleClickOutside);
    return () => window.document.removeEventListener('mousedown', handleClickOutside);
  }, [moreMenuOpen]);

	  const handleAskQuestion = async (e: React.FormEvent) => {
	    e.preventDefault();
	    if (!question.trim() || !id) return;

    if (!hasQaAccess) {
      setUpgradeMessage(t('documents:qa.upgradeMessage'));
      setShowUpgradeModal(true);
      return;
    }

    // Check for unsubstituted template variables
    if (activeTemplateVars.length > 0 && shouldShowTemplateHint(question, activeTemplateVars)) {
      // Don't submit - user needs to substitute the variables first
      return;
    }

	    lastAskedQuestionRef.current = question.trim();
	    setAnswer('');
	    setCurrentAnswerId(null);
	    setAskingQuestion(true);
	    try {
      const userLanguage = i18n.language || 'pt';
      const res = await documentAPI.ask(id, lastAskedQuestionRef.current, userLanguage);
	      setAnswer(res.data.answer);
	      setCurrentAnswerId(res.data.id ?? null);
	      setQaHistory((prev) => [...prev, { ...res.data }]);
	      setQuestion('');
	      setRatingValue(null);
	      setRatingMessage('');
	      setRatingError('');

	      // Refresh the usage counter from backend
	      try {
	        const usageRes = await api.get(`/documents/${id}/usage`);
	        setQuestionsUsedThisMonth(usageRes.data.questions_used || 0);
	      } catch (usageErr) {
	        console.error('Failed to refresh question usage:', usageErr);
	      }
	    } catch (err: any) {
	      // Check if this is a questions limit error
	      const errorDetail = err?.response?.data?.detail;
	      if (errorDetail && typeof errorDetail === 'object' && errorDetail.error === 'questions_limit_reached') {
	        const currentTier = errorDetail.current_tier;
	        const isPlatinum = currentTier === 'PLATINUM';

        if (isPlatinum) {
          // User is on highest tier - show wait message
          setAnswer(t(UPGRADE_MESSAGE_KEYS.QUESTION_LIMIT));
        } else {
          // User can upgrade - show upgrade modal
          setUpgradeMessage(errorDetail.message || t(UPGRADE_MESSAGE_KEYS.QUESTION_LIMIT));
          setShowUpgradeModal(true);
        }
      } else {
        // Generic error
        setAnswer(t('common:errors.generic'));
      }
	      setCurrentAnswerId(null);
	    } finally {
	      setAskingQuestion(false);
	    }
	  };

  const handleDeleteQuestion = async (questionId: number) => {
    if (!id) return;
    if (!window.confirm(t('detail.qa.confirmDelete'))) return;

    setDeletingQuestionId(questionId);
    try {
	      await documentAPI.deleteQuestion(id, questionId);
	      setQaHistory((prev) => prev.filter((qa) => qa.id !== questionId));
	      if (questionId === currentAnswerId) {
	        setAnswer('');
	        setCurrentAnswerId(null);
	        lastAskedQuestionRef.current = '';
	      }

	      // Refresh the usage counter from backend (should stay the same after deletion)
	      try {
	        const usageRes = await api.get(`/documents/${id}/usage`);
	        setQuestionsUsedThisMonth(usageRes.data.questions_used || 0);
	      } catch (usageErr) {
	        console.error('Failed to refresh question usage:', usageErr);
	      }
	    } catch (err) {
	      alert(t('detail.qa.deleteFailed'));
	    } finally {
      setDeletingQuestionId(null);
    }
  };

  const handleRateAnswer = async (value: number, questionId?: number | null) => {
    const targetId = questionId ?? currentAnswerId;
    if (!id || !targetId || ratingSubmitting) return;
    if (!questionId) setRatingValue(value);
    setRatingSubmitting(true);
    setRatingError('');
    try {
      await documentAPI.rateAnswer(id, {
        rating: value,
        questionId: targetId,
      });
      saveStoredRating(id, targetId, value);
      setRatings((prev) => ({ ...prev, [targetId]: value }));
      setQaHistory((prev) =>
        prev.map((qa) => (qa.id === targetId ? { ...qa, rating: value } : qa))
      );
      setRatingMessage(t('detail.qa.thanksFeedback'));
      // Clear the success message after 4 seconds
      setTimeout(() => {
        setRatingMessage('');
      }, 4000);
    } catch (err: any) {
      console.error('Failed to send rating', err);
      setRatingError(t('detail.qa.feedbackError'));
    } finally {
      setRatingSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!id || !window.confirm(t('detail.deleteConfirm.message'))) return;
    try {
      await documentAPI.delete(id);
      navigate('/catalog');
    } catch (err) {
      const detail = await extractErrorMessage(err, t('detail.errors.deleteFailed'));
      alert(detail);
    }
  };

	  // Message Builder handlers
  const toggleRiskSelection = (riskIndex: number) => {
    setSelectedRisks(prev => {
      const newSet = new Set(prev);
      if (newSet.has(riskIndex)) {
        newSet.delete(riskIndex);
      } else {
        newSet.add(riskIndex);
      }
      return newSet;
    });
  };

  const clearSelectedRisks = () => {
    setSelectedRisks(new Set());
  };

  const selectAllRisks = () => {
    const allRiskIndices = risks.map((_: any, index: number) => index);
    setSelectedRisks(new Set(allRiskIndices));
  };

  const handleGenerateMessage = async () => {
    if (!hasMessageBuilder) {
      setUpgradeMessage(t(UPGRADE_MESSAGE_KEYS.MESSAGE_BUILDER));
      setShowUpgradeModal(true);
      return;
    }

    if (selectedRisks.size === 0 || !id) return;

    setGeneratingMessage(true);
    try {
      const selectedRiskData = Array.from(selectedRisks).map(index => {
        const risk = risks[index] || {};
        return {
          index,
          title: (risk as any).title || '',
          negotiationTemplate: (risk as any).negotiation_message_template || '',
          explanation: (risk as any).explanation || (risk as any).potential_impact || '',
          recommendation: (risk as any).recommendation || '',
          severity: (risk as any).severity || ''
        };
      });

      // Generate message using negotiation templates from each risk
      const messageBody = selectedRiskData
        .map((risk, idx) => {
          // Use the AI-generated negotiation template if available, otherwise fallback
          const template = normalizePlaceholderBraces(risk.negotiationTemplate || risk.explanation);
          return `${idx + 1}. ${risk.title}\n   ${template}`;
        })
        .join('\n\n');

      const templates = t('detail.messageBuilder.templates', { returnObjects: true }) as unknown as Array<{ intro: string; closing: string }>;
      const randomTemplate = templates[Math.floor(Math.random() * templates.length)];
      const fullMessage = `${randomTemplate.intro}\n\n${messageBody}\n\n${randomTemplate.closing}`;

      setGeneratedMessage(fullMessage);
      setShowMessageModal(true);
    } catch (error) {
      alert(t('detail.messageBuilder.errors.generate'));
    } finally {
      setGeneratingMessage(false);
    }
  };

  const handleCopyMessage = async () => {
    try {
      await navigator.clipboard.writeText(generatedMessage);
      setMessageCopied(true);
      setTimeout(() => setMessageCopied(false), 2000);

      const pending = Array.from(extractPlaceholders(generatedMessage));
      if (pending.length > 0) {
        const labels = pending.length === 1 ? pending[0] : pending.join(', ');
        setMessageCopyWarning(t('detail.messageBuilder.modal.placeholderWarning', { labels }));
      } else {
        setMessageCopyWarning('');
      }
    } catch (error) {
      alert(t('detail.messageBuilder.errors.copy'));
    }
  };

  const handleDownload = async () => {
    if (!id) return;
    try {
      const { blob, filename } = await fetchDocumentFile();
      triggerFileDownload(blob, filename);
    } catch (err: any) {
      const detail = await extractErrorMessage(err, t('detail.errors.downloadFailed'));
      alert(detail);
    }
  };

  const startRename = () => {
    if (!document) return;
    setIsEditingName(true);
    setNameDraft(document.auto_generated_name);
    setNameError('');
  };

  const cancelRename = () => {
    setIsEditingName(false);
    setNameDraft(document?.auto_generated_name || '');
    setNameError('');
  };

  const saveRename = async () => {
    if (!document || !id) return;
    const trimmed = nameDraft.trim();
    if (!trimmed) {
      setNameError(t('detail.rename.emptyError'));
      return;
    }
    if (trimmed === document.auto_generated_name) {
      cancelRename();
      return;
    }

    setSavingName(true);
    setNameError('');
    try {
      const res = await documentAPI.rename(id, trimmed);
      setDocument({ ...document, auto_generated_name: res.data.name });
      setIsEditingName(false);
    } catch (err: any) {
      setNameError(err?.response?.data?.detail || t('detail.rename.failed'));
    } finally {
      setSavingName(false);
    }
  };

  const buildShareMessage = (link: string) => {
    const name = document?.auto_generated_name || t('detail.share.documentFallback');
    return t('detail.share.messageTemplate', { name, link });
  };

  const getDownloadFilename = (headers: any) => {
    const contentDisposition = headers?.['content-disposition'];
    const filenameMatch = contentDisposition
      ? /filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i.exec(contentDisposition)
      : null;
    return filenameMatch?.[1]
      ? decodeURIComponent(filenameMatch[1])
      : (document?.auto_generated_name || t('detail.share.documentFallback'));
  };

  const extractErrorMessage = async (err: any, fallback: string) => {
    const responseData = err?.response?.data;
    if (responseData instanceof Blob) {
      try {
        const text = await responseData.text();
        const parsed = JSON.parse(text);
        return parsed.detail || fallback;
      } catch (parseErr) {
        // Ignore and fall back.
      }
    }
    return responseData?.detail || fallback;
  };

  const fetchDocumentFile = async () => {
    if (!id) {
      throw new Error(t('detail.errors.invalidDocument'));
    }
    const res = await documentAPI.download(id);
    const contentType = res.headers['content-type'] || 'application/octet-stream';
    const blob = new Blob([res.data], { type: contentType });
    const filename = getDownloadFilename(res.headers);
    return { blob, filename };
  };

  const triggerFileDownload = (blob: Blob, filename: string) => {
    const url = window.URL.createObjectURL(blob);
    const link = window.document.createElement('a');
    link.href = url;
    link.download = filename;
    window.document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const ensureShareLink = async () => {
    if (shareLink) return shareLink;
    if (!id) throw new Error(t('detail.errors.invalidDocument'));
    const res = await documentAPI.shareLink(id);
    setShareLink(res.data.share_url);
    return res.data.share_url;
  };

  const copyShareLink = async () => {
    setShareError('');
    setShareNotice('');
    try {
      const link = await ensureShareLink();
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(link);
        setShareNotice(t('detail.share.linkCopied'));
        return;
      }

      const fallbackInput = window.document.createElement('input');
      fallbackInput.value = link;
      fallbackInput.setAttribute('readonly', 'true');
      fallbackInput.style.position = 'absolute';
      fallbackInput.style.left = '-9999px';
      window.document.body.appendChild(fallbackInput);
      fallbackInput.select();
      fallbackInput.setSelectionRange(0, link.length);
      const copied = window.document.execCommand('copy');
      window.document.body.removeChild(fallbackInput);
      if (!copied) {
        throw new Error('Clipboard not available');
      }
      setShareNotice(t('detail.share.linkCopied'));
    } catch (err) {
      setShareError(t('detail.share.copyError'));
    }
  };

  const handleShareOption = async (channel: 'whatsapp') => {
    setShareError('');
    setShareNotice('');
    setSharing(true);
    try {
      const link = await ensureShareLink();
      const message = buildShareMessage(link);
      const encodedMessage = encodeURIComponent(message);
      if (channel === 'whatsapp') {
        window.open(`https://wa.me/?text=${encodedMessage}`, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      const detail = await extractErrorMessage(err, t('detail.errors.shareFailed'));
      setShareError(detail);
    } finally {
      setSharing(false);
    }
  };



  const userRole = document?.user_role || 'tenant';
  const suggestedQuestionsRaw = t(`detail.questions.suggestedQuestions.${userRole}`, { returnObjects: true });
  const suggestedQuestions = Array.isArray(suggestedQuestionsRaw) ? (suggestedQuestionsRaw as string[]) : [];
  const riskDisclaimer = t('documents:detail.risks.disclaimer', { defaultValue: RISK_DISCLAIMER });
  const resizeQuestionInput = useCallback(() => {
    const input = questionInputRef.current;
    if (!input) return;
    input.style.height = 'auto';
    input.style.height = `${Math.min(input.scrollHeight, 160)}px`;
  }, []);

  useEffect(() => {
    resizeQuestionInput();
  }, [question, resizeQuestionInput]);

  if (loading && !document) {
    return (
      <div className="document-detail-new">
        <div className="loading-wrapper">
          <div className="loading-card">
            {/* <div className="loading-icon"> */}
              {/* <RefreshCwIcon className="spin" size={32} /> */}
            {/* </div> */}
            <h2>{t('detail.processing.title')}</h2>
            <p>{t('detail.processing.subtitle')}</p>
            <div className="loading-progress">
              <div className="loading-progress-bar" />
            </div>
            <div className="loading-meta">
              <span className="dot" />
              <span className="dot" />
              <span className="dot" />
            </div>
          </div>
        </div>
        <footer className="document-footer">
          <div className="document-footer-content">
            <div className="footer-left">
              <img src="/logo_transp.png" alt="ContratoSeguro" className="footer-logo" />
              <div className="footer-text">
                <span className="footer-copyright">© {new Date().getFullYear()} ContratoSeguro</span>
                <span className="footer-tagline">{t('common:footer.tagline')}</span>
              </div>
            </div>
            <div className="footer-right">
              <span className="footer-tech">Powered by AI</span>
            </div>
          </div>
        </footer>
      </div>
    );
  }

  if (!document) {
    return (
      <div className="error-container">
        <div className="error-icon"><FileTextIcon size={48} /></div>
        <h2>{t('detail.error.title')}</h2>
        <button onClick={() => navigate(getLocalizedPath('/catalog'))} className="btn-primary">
          {t('detail.backToCatalog')}
        </button>
      </div>
    );
  }

  const normalizeSummary = (raw: any) => {
    if (!raw) return {};

    let parsed = raw;

    if (typeof raw === 'string') {
      try {
        parsed = JSON.parse(raw);
      } catch {
        parsed = {};
      }
    }

    if (Array.isArray(parsed)) {
      const converted: Record<string, any> = {};
      parsed.forEach((item: any, idx: number) => {
        if (item && typeof item === 'object') {
          const label = item.label || item.nome || `campo_${idx}`;
          const key = label.toString().trim().toLowerCase().replace(/\s+/g, '_');
          converted[key] = item.value ?? item.valor ?? item.text ?? item.descricao;
        }
      });
      parsed = converted;
    }

    const base = parsed?.mandatory_fields || parsed || {};
    const normalized: any = { ...base };
    const mappings: Record<string, string> = {
      rental_value: 'valor_aluguel',
      valor: 'valor_aluguel',
      rent_value: 'valor_aluguel',
      duration: 'prazo_locacao',
      contract_duration: 'prazo_locacao',
      start_date: 'data_inicio',
      end_date: 'data_termino',
      adjustment_index: 'reajuste',
      reajuste: 'reajuste',
      guarantee: 'garantia',
      guarantees: 'garantia',
      penalties: 'multas',
      fines: 'multas',
      penalty: 'multas',
      payment_terms: 'pagamento',
      payment: 'pagamento',
      condominium_fees: 'condominio',
      condo_fee: 'condominio',
      pets: 'animais_permitidos',
      animais: 'animais_permitidos',
      repairs: 'reparos',
      maintenance: 'reparos',
      inspection: 'vistoria',
      uso_imovel: 'uso_imovel',
    };

    Object.entries(mappings).forEach(([source, target]) => {
      if (base[source] && !normalized[target]) {
        if (target === 'reajuste' && typeof base[source] === 'string') {
          normalized[target] = { indice: base[source] };
        } else {
          normalized[target] = base[source];
        }
      }
    });

    return normalized;
  };

  const analysisRaw = document.analysis || {};
  const analysis =
    analysisRaw.structured_analysis ||
    analysisRaw.result ||
    analysisRaw.full_analysis ||
    analysisRaw;
  const summary = normalizeSummary(analysis.summary || analysis.full_summary);

  // Risks are already sorted by severity on the backend (high -> medium -> low)
  const risks = analysis.risks || [];

  const partiesData = analysis.parties?.parties || analysis.parties || {};
  const partyCards = buildPartyCards(partiesData);
	  const visibleHistory = qaHistory
	    .filter((qa) => qa.id !== currentAnswerId)
	    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
	  const currentQa = currentAnswerId ? qaHistory.find((qa) => qa.id === currentAnswerId) : null;
	  const currentQuestionText = currentQa?.question || (answer ? lastAskedQuestionRef.current : '');
	  const shouldShowTemplateHelp = activeTemplateVars.length > 0 && shouldShowTemplateHint(question, activeTemplateVars);

	  const formatHistoryDate = (value?: string) => {
    if (!value) return t('detail.values.dateUnavailable');
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return t('detail.values.dateUnavailable');
    return parsed.toLocaleString(i18n.language === 'en' ? 'en-US' : 'pt-BR');
  };

  // Calculate risk statistics
  const riskCounts = {
    high: risks.filter((r: any) => r.severity === 'high').length,
    medium: risks.filter((r: any) => r.severity === 'medium').length,
    low: risks.filter((r: any) => r.severity === 'low').length,
  };

  const totalRisks = riskCounts.high + riskCounts.medium + riskCounts.low;

  const allSliderItems = risks.map((risk: any, i: number) => ({
    risk,
    originalIndex: i,
    isLocked: risk.locked || (!hasRiskDetails && !readOnly),
  }));
  const sliderItems = selectedFilter
    ? allSliderItems.filter((item: { risk: any; originalIndex: number; isLocked: boolean }) => item.risk.severity === selectedFilter)
    : allSliderItems;

  const floatingNavItems: FloatingNavItem[] = [
    totalRisks > 0 ? { id: 'section-risks-overview', label: t('detail.tabs.risks'), icon: <AlertIcon size={18} /> } : null,
    Object.keys(summary).length > 0 ? { id: 'section-summary', label: t('detail.tabs.summary'), icon: <ClipboardIcon size={18} /> } : null,
    !isProcessing ? { id: 'section-qa', label: t('detail.tabs.questions'), icon: <ChatIcon size={18} /> } : null,
  ].filter(Boolean) as FloatingNavItem[];

  const floatingNavTranslations: FloatingNavTranslations = {
    sectionsLabel: t('detail.floatingNav.sections'),
    hoverHint: t('detail.floatingNav.hoverHint'),
    documentNav: t('detail.floatingNav.sections'),
    documentSections: t('detail.floatingNav.sections'),
  };

  return (
    <div className="document-detail-new">
      {processingError && (
        <div className="processing-error-banner">
          <div className="processing-error-card">
            <div className="processing-error-icon">
              <AlertCircleIcon size={24} />
            </div>
            <div className="processing-error-text">
              <h3>{t('detail.processingError.title')}</h3>
              <p>{processingError}</p>
            </div>
            <div className="processing-error-actions">
              <button
                type="button"
                className="processing-error-btn primary"
                onClick={async () => {
                  try {
                    if (id) {
                      await api.delete(`/documents/${id}`);
                    }
                  } catch (e) {
                    console.error('Failed to delete invalid document', e);
                  } finally {
                    setProcessingError(null);
                    navigate(getLocalizedPath('/catalog'));
                  }
                }}
              >
                {t('common:buttons.close')}
              </button>
            </div>
          </div>
        </div>
      )}
      <div className={`page-content ${processingError ? 'blurred' : ''}`}>
      {/* Header */}
      <div className="detail-header-new">
        <div className="header-content">
          {/* Utility bar: back link lives above the H1 */}
          <div className="header-utility-bar">
            <button onClick={() => navigate(getLocalizedPath('/catalog'))} className="btn-nav">
              ← {t('common:buttons.back')}
            </button>
          </div>

          {/* Main row: title + actions */}
          <div className="header-main-row">
          <div className="header-left">
            <div className="document-title-row">
              {isEditingName ? (
                <div className="document-title-edit">
                  <input
                    className="document-title-input"
                    value={nameDraft}
                    onChange={(event) => {
                      setNameDraft(event.target.value);
                      if (nameError) {
                        setNameError('');
                      }
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        saveRename();
                      }
                      if (event.key === 'Escape') {
                        event.preventDefault();
                        cancelRename();
                      }
                    }}
                    maxLength={80}
                    disabled={savingName}
                    autoFocus
                  />
                  <div className="document-title-actions">
                    <button
                      className="document-title-btn save"
                      type="button"
                      onClick={saveRename}
                      disabled={savingName}
                    >
                      <CheckIcon size={16} />
                    </button>
                    <button
                      className="document-title-btn cancel"
                      type="button"
                      onClick={cancelRename}
                      disabled={savingName}
                    >
                      <CloseIcon size={16} />
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <h1 className="document-title-new">{document.auto_generated_name}</h1>
                  <button
                    className="document-title-btn edit"
                    type="button"
                    onClick={startRename}
                  >
                    <EditIcon size={16} />
                  </button>
                </>
              )}
            </div>
            {nameError && <div className="document-title-error">{nameError}</div>}
            <div className="document-meta-new">
              <span className="meta-item-new">
                <CalendarIcon size={16} />
                {new Date(document.created_at).toLocaleDateString('pt-BR')}
              </span>
              <span className={`status-badge status-${document.status}`}>
                {document.status === 'completed' && <CheckCircleIcon size={14} />}
                {document.status === 'processing' && <LoaderIcon size={14} className="status-icon-spin" />}
                {document.status === 'failed' && <AlertCircleIcon size={14} />}
                {document.status === 'completed' ? t('common:status.completed') :
                 document.status === 'processing' ? t('common:status.processing') :
                 document.status === 'failed' ? t('common:status.failed') : document.status}
              </span>
              {document.user_role && (
                <span className={`perspective-badge perspective-${document.user_role}`}>
                  <UserIcon size={14} />
                  {document.user_role === 'tenant' ? t('common:roles.tenant') : t('common:roles.landlord')}
                </span>
              )}
            </div>
          </div>

          {/* Action Group - Right Side */}
          <div className="header-actions-right">
            {/* Notifications CTA */}
            <button
              type="button"
              className="notifications-cta"
              onClick={() => setShowNotificationsModal(true)}
              disabled={isProcessing}
            >
              <ClockIcon size={18} />
              <span>{notificationsEnabled ? t('detail.notifications.ctaManage') : t('detail.notifications.cta')}</span>
              <ChevronRightIcon size={16} />
            </button>

            {/* ⋮ Menu — download + share + delete consolidated (max 2 header elements) */}
            <div className="more-menu" ref={moreMenuRef}>
              <button
                onClick={() => setMoreMenuOpen((prev) => !prev)}
                className="btn-more"
                type="button"
                aria-label={t('common:buttons.moreActions')}
              >
                <MoreIcon size={20} />
              </button>
              {moreMenuOpen && (
                <div className="more-menu-dropdown">
                    {/* Download — folded in from top bar */}
                    <button onClick={handleDownload} className="more-menu-item" type="button">
                      <DownloadIcon size={18} />
                      <span>{t('detail.download.button')}</span>
                    </button>

                    {/* Share Action */}
                    <div className="share-menu-inline" ref={shareMenuRef}>
                      <button
                        onClick={() => setShareOpen((prev) => !prev)}
                        className="more-menu-item"
                        type="button"
                      >
                        <ShareIcon size={18} />
                        <span>{t('detail.share.button')}</span>
                      </button>
                      {shareOpen && (
                        <div className="share-panel">
                          <div className="share-hint">
                            {t('detail.share.downloadHint')}
                          </div>
                          <div className="share-options">
                            <button
                              type="button"
                              className="share-option"
                              onClick={() => handleShareOption('whatsapp')}
                              disabled={sharing}
                            >
                              <span className="share-option-icon whatsapp">W</span>
                              WhatsApp
                            </button>
                            <button
                              type="button"
                              className="share-option share-copy"
                              onClick={copyShareLink}
                              disabled={sharing}
                            >
                              {t('detail.share.copyLink')}
                            </button>
                          </div>
                          {shareNotice && <div className="share-notice">{shareNotice}</div>}
                          {shareError && <div className="share-error">{shareError}</div>}
                        </div>
                      )}
                    </div>

                    {/* Delete Action - Destructive */}
                    <button onClick={handleDelete} className="more-menu-item more-menu-item-danger">
                      <TrashIcon size={18} />
                      <span>{t('detail.actions.delete')}</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div> {/* header-main-row */}
        </div> {/* header-content */}
      </div>
      <div className={`page-body ${processingError ? 'blurred' : ''}`}>
      <FloatingSectionNav items={floatingNavItems} translations={floatingNavTranslations} />

      {/* Main Content */}
      <div className="detail-content-new">

	        {/* Risk Overview Section */}
	        {totalRisks > 0 && (
	          <section id="section-risks-overview" className="detail-section section-tone-1">
	            <SectionHeader
	              icon={<AlertIcon size={24} />}
	              title={t('detail.risks.title')}
	              subtitle={t('detail.risks.subtitle')}
            />

            {/* One-liner summary + filter chips */}
            {(() => {
              const totalBrl = risks
                .filter((r: any) => typeof r.financial_impact_brl === 'number' && r.financial_impact_brl > 0)
                .reduce((sum: number, r: any) => sum + r.financial_impact_brl, 0);
              const formatted = totalBrl > 0
                ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(totalBrl)
                : null;
              return (
                <>
                  <div className="risk-one-liner">
                    <span className="risk-one-liner-count">
                      {t('detail.risks.oneliner.points', { count: totalRisks })}
                    </span>
                    {riskCounts.high > 0 && (
                      <>
                        <span className="risk-one-liner-sep">·</span>
                        <span className="risk-one-liner-critical">
                          {t('detail.risks.oneliner.critical', { count: riskCounts.high })}
                        </span>
                      </>
                    )}
                    {formatted && (
                      <>
                        <span className="risk-one-liner-sep">·</span>
                        <span className="risk-one-liner-impact">{t('detail.risks.oneliner.estimatedImpact', { amount: formatted })}</span>
                      </>
                    )}
                  </div>
                  <div className="risk-filter-chips">
                    <button
                      type="button"
                      className={`risk-filter-chip${!selectedFilter ? ' active' : ''}`}
                      onClick={() => setSelectedFilter(null)}
                    >
                      {t('detail.risks.filterChips.all', { count: totalRisks })}
                    </button>
                    {riskCounts.high > 0 && (
                      <button
                        type="button"
                        className={`risk-filter-chip chip-high${selectedFilter === 'high' ? ' active' : ''}`}
                        onClick={() => setSelectedFilter(prev => prev === 'high' ? null : 'high')}
                      >
                        {t('detail.risks.filterChips.critical', { count: riskCounts.high })}
                      </button>
                    )}
                    {riskCounts.medium > 0 && (
                      <button
                        type="button"
                        className={`risk-filter-chip chip-medium${selectedFilter === 'medium' ? ' active' : ''}`}
                        onClick={() => setSelectedFilter(prev => prev === 'medium' ? null : 'medium')}
                      >
                        {t('detail.risks.filterChips.moderate', { count: riskCounts.medium })}
                      </button>
                    )}
                    {riskCounts.low > 0 && (
                      <button
                        type="button"
                        className={`risk-filter-chip chip-low${selectedFilter === 'low' ? ' active' : ''}`}
                        onClick={() => setSelectedFilter(prev => prev === 'low' ? null : 'low')}
                      >
                        {t('detail.risks.filterChips.low', { count: riskCounts.low })}
                      </button>
                    )}
                  </div>
                </>
              );
            })()}

            {/* Always-visible risk slider (filtered by selected severity) */}
            {risks.length > 0 && (
              <>
                {/* Draft message chip — visible when any risk is selected */}
                {hasMessageBuilder && selectedRisks.size > 0 && (
                  <div className="risk-draft-chip">
                    <span className="risk-draft-chip-dot">●</span>
                    <span>
                      {t('detail.risks.draftChip.label')} · <strong>{t('detail.risks.draftChip.points', { count: selectedRisks.size })}</strong>
                    </span>
                    <button
                      type="button"
                      className="risk-draft-chip-action"
                      onClick={handleGenerateMessage}
                      disabled={generatingMessage}
                    >
                      {generatingMessage ? t('detail.risks.draftChip.generating') : t('detail.risks.draftChip.view')}
                    </button>
                  </div>
                )}
                <div className="risk-sections">
                  <RiskSlider
                    key={selectedFilter ?? 'all'}
                    items={sliderItems}
                    translations={riskAccordionTranslations}
                    selectedRisks={selectedRisks}
                    hasMessageBuilder={hasMessageBuilder}
                    onToggle={(_originalIndex, isLocked) => {
                      if (isLocked) {
                        setUpgradeMessage(t(UPGRADE_MESSAGE_KEYS.LOCKED_FEATURE));
                        setShowUpgradeModal(true);
                      }
                    }}
                    onSelectionToggle={toggleRiskSelection}
                    documentId={document?.id}
                  />
                  <div className="risk-disclaimer"><span>{riskDisclaimer}</span></div>
                </div>
              </>
            )}
          </section>
        )}

	        {/* Summary Section */}
	        {Object.keys(summary).length > 0 && (
	          <section id="section-summary" className="detail-section section-tone-2">
	            <SectionHeader
	              icon={<ClipboardIcon size={24} />}
	              title={t('detail.summary.title')}
	              subtitle={t('detail.summary.subtitle')}
            />

            {/* Tier 1: Always visible priority cards */}
            <div className="summary-grid-new summary-tier-1">
              {renderSummaryCards(summary, 'tier1')}
            </div>

            {/* Expand/Collapse Button */}
            {renderSummaryCards(summary, 'tier2').length > 0 && (
              <button
                onClick={() => setSummaryExpanded(!summaryExpanded)}
                className="summary-expand-btn"
                aria-expanded={summaryExpanded}
                aria-controls="summary-tier-2"
              >
                <span>{summaryExpanded ? t('detail.summary.collapseDetails') : t('detail.summary.expandDetails')}</span>
                {summaryExpanded ? <ChevronUpIcon size={20} /> : <ChevronDownIcon size={20} />}
              </button>
            )}

            {/* Tier 2: Expandable secondary cards */}
            {summaryExpanded && (
              <div
                id="summary-tier-2"
                className="summary-grid-new summary-tier-2"
                role="region"
                aria-label={t('detail.summary.additionalDetailsAria')}
              >
                {renderSummaryCards(summary, 'tier2')}
              </div>
            )}

            {/* Contract Parties Subsection */}
            {partyCards.length > 0 && (
              <div className="summary-parties">
                <div className="summary-parties-header">
                  <h3>{t('detail.parties.title')}</h3>
                  <p>{t('detail.parties.subtitle')}</p>
                </div>
                <div className="parties-grid">
                  {partyCards}
                </div>
              </div>
            )}
          </section>
        )}

        {/* Message Builder Action Bar */}
        {hasMessageBuilder && selectedRisks.size > 0 && (
          <div className="message-builder-bar">
            <div className="message-builder-bar-content">
              <span className="message-builder-count">
                {t('detail.messageBuilder.selectedCount', { count: selectedRisks.size })}
              </span>
              <div className="message-builder-actions">
                <button
                  type="button"
                  className="message-builder-select-all-btn"
                  onClick={selectAllRisks}
                  disabled={generatingMessage || selectedRisks.size === risks.length}
                >
                  {t('detail.messageBuilder.selectAll')}
                </button>
                <button
                  type="button"
                  className="message-builder-clear-btn"
                  onClick={clearSelectedRisks}
                  disabled={generatingMessage}
                >
                  {t('detail.messageBuilder.clearAll')}
                </button>
                <button
                  className="message-builder-generate-btn"
                  onClick={handleGenerateMessage}
                  disabled={generatingMessage}
                >
                  {generatingMessage ? (
                    <>{t('detail.messageBuilder.generating')}</>
                  ) : (
                    <>{t('detail.messageBuilder.generate')}</>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

	        {/* Q&A Section */}
	        {!isProcessing && (
	        <section id="section-qa" className="detail-section section-tone-5">
	          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
	            <SectionHeader
	              icon={<ChatIcon size={24} />}
	              title={t('detail.questions.title')}
              subtitle={t('detail.questions.subtitle')}
            />
            {hasQaAccess ? (
              (() => {
                const isNearLimit = questionsUsedThisMonth >= questionsLimitPerContractMonth * 0.8;
                const isAtLimit = questionsUsedThisMonth >= questionsLimitPerContractMonth;

                return (
                  <div style={{
                    fontSize: '0.85rem',
                    color: isAtLimit ? '#dc2626' : isNearLimit ? '#ea580c' : '#64748b',
                    fontWeight: '500',
                    textAlign: 'right',
                    padding: '0.5rem 0'
                  }}>
                    {t('detail.qa.questionsUsed', { used: questionsUsedThisMonth, limit: questionsLimitPerContractMonth })}
                  </div>
                );
              })()
            ) : (
              <div style={{
                padding: '0.5rem 0.75rem',
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                fontSize: '0.85rem',
                color: '#475569',
                minWidth: '140px',
                textAlign: 'center',
                fontWeight: 600
              }}>
                {`${t('common:subscription.premium')} / ${t('common:subscription.platinum')}`}
              </div>
            )}
          </div>
          <div className="qa-container-new">
            {hasQaAccess ? (
              <>
	                {/* Suggested questions */}
                {suggestedQuestions.length > 0 && (
                  <div className="qa-suggested-questions">
                    {suggestedQuestions.map((suggestion, index) => (
                      <button
                        key={index}
                        type="button"
		                        className="qa-suggestion-chip"
		                        onClick={() => {
		                          const stripped = stripTemplateBraces(suggestion);
		                          setQuestion(stripped.text);
		                          setActiveTemplateVars(stripped.vars);
		                          window.setTimeout(() => {
		                            questionInputRef.current?.focus();
		                            const first = stripped.ranges[0];
		                            if (first) {
		                              questionInputRef.current?.setSelectionRange(first.start, first.end);
		                            } else {
		                              questionInputRef.current?.setSelectionRange(stripped.text.length, stripped.text.length);
		                            }
		                          }, 0);
		                        }}
		                        disabled={askingQuestion}
		                      >
		                        {renderSuggestedQuestionLabel(suggestion)}
		                      </button>
	                    ))}
	                  </div>
	                )}

                <form onSubmit={handleAskQuestion} className="qa-form-new">
	                  <textarea
	                    ref={questionInputRef}
	                    className="qa-input-new"
	                    placeholder={t('detail.questions.placeholder')}
	                    value={question}
                      rows={1}
	                    onChange={(e) => {
	                      const next = e.target.value;
	                      setQuestion(next);
	                      if (activeTemplateVars.length > 0 && !shouldShowTemplateHint(next, activeTemplateVars)) {
	                        setActiveTemplateVars([]);
	                      }
	                    }}
                      onInput={resizeQuestionInput}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                          e.preventDefault();
                          if (!askingQuestion && question.trim()) {
                            e.currentTarget.form?.requestSubmit();
                          }
                        }
                      }}
	                    disabled={askingQuestion}
	                  />
                  <button
                    type="submit"
                    className="qa-submit-new"
                    disabled={askingQuestion || !question.trim()}
                  >
                    {askingQuestion ? <RefreshCwIcon size={18} className="animate-spin" /> : <ArrowRightIcon size={18} />}
	                  </button>
	                </form>

	                {shouldShowTemplateHelp && (
	                  <div className="qa-template-error">
	                    <AlertCircleIcon size={14} />
	                    <span>{activeTemplateVars.length === 1
	                      ? t('detail.questions.templateErrorSingle', { var: activeTemplateVars[0] })
	                      : t('detail.questions.templateErrorMultiple', { vars: activeTemplateVars.join(', ') })}</span>
	                  </div>
	                )}

	                {/* Context awareness helper text */}
	                {!shouldShowTemplateHelp && (
	                  <p className="qa-context-helper">{t('detail.questions.contextHelper')}</p>
	                )}
	              </>
	            ) : (
	              <div style={{
                background: '#ffffff',
                border: '1px dashed #cbd5e1',
                borderRadius: '12px',
                padding: '1rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '1rem'
              }}>
                <div style={{ color: '#475569', lineHeight: 1.4 }}>
                  <div style={{ fontWeight: 700, color: '#0f172a' }}>{t('detail.questions.title')}</div>
                  <div style={{ fontSize: '0.95rem' }}>{t('detail.qa.upgradeMessage')}</div>
                </div>
                <button
                  type="button"
                  className="btn-primary-landing"
                  onClick={() => {
                    setUpgradeMessage(t(UPGRADE_MESSAGE_KEYS.LOCKED_FEATURE));
                    setShowUpgradeModal(true);
                  }}
                >
                  {t('common:subscription.upgrade')}
                </button>
              </div>
            )}

            {answer && (
              <div className="qa-answer-new">
                <div className="qa-answer-header">
                  <span className="qa-answer-label">{t('detail.questions.history.answerLabel')}</span>
                  <div className="qa-answer-actions">
                    <div className="qa-answer-meta">
                      <ChatIcon size={16} />
                      <span>{t('detail.questions.aiGenerated')}</span>
                    </div>
                    {currentAnswerId && (
                      <button
                        type="button"
                        className="qa-delete-button"
                        onClick={() => handleDeleteQuestion(currentAnswerId)}
                        disabled={deletingQuestionId === currentAnswerId}
                        aria-label={t('detail.questions.history.delete')}
                      >
                        <TrashIcon size={14} />
                        {t('detail.questions.history.delete')}
                      </button>
                    )}
	                  </div>
	                </div>
                {currentQuestionText && (
                  <p className="qa-answer-question">{t('detail.questions.history.questionLabel')} - {currentQuestionText}</p>
                )}
                {(() => {
                  const { body, excerpt } = extractAnswerExcerpt(answer);
                  const excerptKey = `current-${currentAnswerId ?? 'temp'}`;
                  const isOpen = excerptOpenKeys.has(excerptKey);
                  const excerptItems = splitExcerptItems(excerpt);
                  return (
                    <>
                      <div className="qa-answer-text qa-markdown">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                          {body}
                        </ReactMarkdown>
                      </div>
                      {excerptItems.length > 0 && (
                        <div className="qa-excerpt-block">
                          <button
                            type="button"
                            className="qa-excerpt-toggle"
                            onClick={() => toggleExcerpt(excerptKey)}
                            aria-expanded={isOpen}
                          >
                            {isOpen ? t('detail.questions.excerpt.hide') : t('detail.questions.excerpt.show')}
                          </button>
                          {isOpen && (
                            <div className="qa-excerpt-box qa-markdown">
                              {excerptItems.length > 1 ? (
                                <ul className="qa-excerpt-list">
                                  {excerptItems.map((item, idx) => (
                                    <li key={`${excerptKey}-${idx}`}>
                                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                        {item}
                                      </ReactMarkdown>
                                    </li>
                                  ))}
                                </ul>
                              ) : (
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                  {excerptItems[0]}
                                </ReactMarkdown>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  );
                })()}
                <div className="qa-rating-card">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {([1, 2, 3, 4, 5] as const).map((value) => (
                      <button
                        key={value}
                        type="button"
                        className="qa-rating-star"
                        onClick={() => handleRateAnswer(value)}
                        disabled={ratingSubmitting}
                        aria-label={t('detail.questions.rateLabel', { value })}
                      >
                        <StarIcon
                          size={24}
                          color={ratingValue && ratingValue >= value ? '#f59e0b' : '#cbd5e1'}
                          fill={ratingValue && ratingValue >= value ? '#f59e0b' : 'none'}
                          strokeWidth={2}
                        />
                      </button>
                    ))}
                    {ratingSubmitting && <span style={{ fontSize: '13px', color: '#475569' }}>{t('common:buttons.sending')}</span>}
                  </div>
                  {ratingMessage && <div className="qa-rating-success">{ratingMessage}</div>}
                  {ratingError && <div className="qa-rating-error">{ratingError}</div>}
                </div>
	              </div>
	            )}

            {hasQaAccess && visibleHistory.length > 0 && (
              <div className="qa-history">
                <div className="qa-history-header">
                  <div className="qa-history-title">
                    <ChatIcon size={18} />
                    <span>{t('detail.questions.history.title')}</span>
                  </div>
                  <span className="qa-history-count">{visibleHistory.length}</span>
                </div>
                <div className="qa-history-list">
                  {visibleHistory.map((qa) => (
                    <div className="qa-history-item" key={qa.id}>
                      <div className="qa-history-meta">
                        <span className="qa-history-label">{t('detail.questions.history.questionLabel')}</span>
                        <div className="qa-history-actions">
                          <span className="qa-history-time">
                            {formatHistoryDate(qa.created_at)}
                          </span>
                          <button
                            type="button"
                            className="qa-delete-button"
                            onClick={() => handleDeleteQuestion(qa.id)}
                            disabled={deletingQuestionId === qa.id}
                            aria-label={t('detail.questions.history.delete')}
                          >
                            <TrashIcon size={14} />
                            {t('detail.questions.history.delete')}
                          </button>
                        </div>
                      </div>
                      <p className="qa-history-question">{qa.question}</p>
                      {(() => {
                        const { body, excerpt } = extractAnswerExcerpt(qa.answer);
                        const excerptKey = `history-${qa.id}`;
                        const isOpen = excerptOpenKeys.has(excerptKey);
                        const excerptItems = splitExcerptItems(excerpt);
                        return (
                          <>
                            <div className="qa-history-answer qa-markdown">
                              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                {body}
                              </ReactMarkdown>
                            </div>
                            {excerptItems.length > 0 && (
                              <div className="qa-excerpt-block">
                                <button
                                  type="button"
                                  className="qa-excerpt-toggle"
                                  onClick={() => toggleExcerpt(excerptKey)}
                                  aria-expanded={isOpen}
                                >
                                  {isOpen ? t('detail.questions.excerpt.hide') : t('detail.questions.excerpt.show')}
                                </button>
                                {isOpen && (
                                  <div className="qa-excerpt-box qa-markdown">
                                    {excerptItems.length > 1 ? (
                                      <ul className="qa-excerpt-list">
                                        {excerptItems.map((item, idx) => (
                                          <li key={`${excerptKey}-${idx}`}>
                                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                              {item}
                                            </ReactMarkdown>
                                          </li>
                                        ))}
                                      </ul>
                                    ) : (
                                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                        {excerptItems[0]}
                                      </ReactMarkdown>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                          </>
                        );
                      })()}
                      <div className="qa-history-rating">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          {([1, 2, 3, 4, 5] as const).map((value) => (
                            <button
                              key={value}
                              type="button"
                              className="qa-rating-star"
                              onClick={() => handleRateAnswer(value, qa.id)}
                              disabled={ratingSubmitting}
                              aria-label={`Dar nota ${value}`}
                            >
                              <StarIcon
                                size={18}
                                color={ratings[qa.id] && ratings[qa.id] >= value ? '#f59e0b' : '#cbd5e1'}
                                fill={ratings[qa.id] && ratings[qa.id] >= value ? '#f59e0b' : 'none'}
                                strokeWidth={2}
                              />
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
        )}


        {/* Processing banner - shown at bottom when analysis is still in progress */}
        {isProcessing && (
          <section className="detail-section section-tone-1 processing-inline">
            <ContractProcessingCard
              title={t('detail.processing.title')}
              stepTitle={processingStages[processingStage].title}
              stepIcon={processingStages[processingStage].icon}
              motivation={selectedMotivation}
            />
          </section>
      )}

      </div>
      <footer className="document-footer">
        <div className="document-footer-content">
          <div className="footer-left">
            <img src="/logo_transp.png" alt="ContratoSeguro" className="footer-logo" />
            <div className="footer-text">
              <span className="footer-copyright">© {new Date().getFullYear()} ContratoSeguro</span>
              <span className="footer-tagline">{t('common:footer.tagline')}</span>
            </div>
          </div>
          <div className="footer-right">
            <span className="footer-tech">{t('common:footer.poweredBy')}</span>
          </div>
        </div>
      </footer>
      </div>
      </div>

      {/* Notifications Modal */}
      {showNotificationsModal && (
        <div
          className="notifications-modal-backdrop"
          onClick={() => setShowNotificationsModal(false)}
          role="dialog"
          aria-modal="true"
          aria-label={t('notifications:settings.title', { defaultValue: 'Contract notifications' })}
        >
          <div
            className="notifications-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="notifications-modal-close floating"
              onClick={() => setShowNotificationsModal(false)}
              aria-label={t('common:buttons.close')}
            >
              <CloseIcon size={18} />
            </button>
            <div className="notifications-modal-body">
              <NotificationSettings
                documentId={document.id}
                locked={!hasNotifications}
                onLockedAction={() => {
                  setUpgradeMessage(t('detail.notifications.upgradeMessage'));
                  setShowUpgradeModal(true);
                }}
                onEnabledChange={setNotificationsEnabled}
              />
            </div>
          </div>
        </div>
      )}

      {/* Message Builder Modal */}
      {showMessageModal && (
        <div className="message-builder-modal" onClick={() => setShowMessageModal(false)}>
          <div className="message-builder-content" onClick={(e) => e.stopPropagation()}>
            <h2 className="message-builder-title">{t('detail.messageBuilder.modal.title')}</h2>

            <p className="message-builder-hint">
              {t('detail.messageBuilder.modal.placeholderNote')}
            </p>

            <div className="message-builder-textarea-container">
              <div
                className="message-builder-textarea-backdrop"
                aria-hidden="true"
              >
                {generatedMessage.split(/(\{\{[^}]+\}\}|\{[^}]+\})/g).map((part, idx) => {
                  if (part.match(/^\{\{[^}]+\}\}$/) || part.match(/^\{[^}]+\}$/)) {
                    return <mark key={idx} className="placeholder-highlight">{part}</mark>;
                  }
                  return <span key={idx}>{part}</span>;
                })}
              </div>
              <textarea
                className="message-builder-textarea"
                value={generatedMessage}
                onChange={(e) => {
                  setGeneratedMessage(e.target.value);
                  if (messageCopyWarning) setMessageCopyWarning('');
                }}
                rows={15}
              />
            </div>

            <div className="message-builder-actions">
              <button
                className="message-builder-copy-btn"
                onClick={handleCopyMessage}
              >
                {messageCopied ? `✓ ${t('detail.messageBuilder.modal.copied')}` : t('detail.messageBuilder.modal.copy')}
              </button>
              <button
                className="message-builder-close-btn"
                onClick={() => setShowMessageModal(false)}
              >
                {t('detail.messageBuilder.modal.close')}
              </button>
            </div>

            {messageCopyWarning && (
              <div className="message-builder-copy-warning" role="status">
                <AlertIcon size={16} />
                <span>{messageCopyWarning}</span>
              </div>
            )}
          </div>
        </div>
      )}

      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        message={upgradeMessage}
        onUpgradeSuccess={() => {
          // Reload the page to show updated UI without blurred elements
          window.location.reload();
        }}
      />
    </div>
  );
};

const normalizeLegalReferenceParts = (reference: unknown): string[] => {
  if (reference === null || reference === undefined) return [];

  if (typeof reference === 'string') {
    const trimmed = reference.trim();
    if (!trimmed) return [];
    return trimmed
      .split(/\n|;|•/g)
      .map((part) => part.trim())
      .filter(Boolean);
  }

  if (Array.isArray(reference)) {
    const parts: string[] = [];
    reference.forEach((item) => {
      parts.push(...normalizeLegalReferenceParts(item));
    });
    return parts;
  }

  if (typeof reference === 'object') {
    const maybeText =
      (reference as any).text ??
      (reference as any).reference ??
      (reference as any).value ??
      (reference as any).name;
    if (typeof maybeText === 'string') {
      return normalizeLegalReferenceParts(maybeText);
    }
    try {
      return [JSON.stringify(reference)];
    } catch {
      return [String(reference)];
    }
  }

  return [String(reference)];
};

const parseLegalReferenceJsonString = (
  referenceText: string
): { label: string; url?: string | null } | null => {
  const trimmed = referenceText.trim();
  if (!trimmed.startsWith('{') || !trimmed.endsWith('}')) return null;

  try {
    const parsed = JSON.parse(trimmed);
    if (!parsed || typeof parsed !== 'object') return null;

    const law = (parsed as any).law ?? (parsed as any).lei ?? (parsed as any).law_name ?? null;
    const article = (parsed as any).article ?? (parsed as any).artigo ?? (parsed as any).art ?? null;
    const url = (parsed as any).url ?? (parsed as any).link ?? null;

    const parts = [law, article].filter((v) => typeof v === 'string' && v.trim().length > 0) as string[];
    const label = parts.length > 0 ? parts.join(', ') : trimmed;

    if (typeof url === 'string' && url.trim().length > 0) {
      return { label, url: url.trim() };
    }

    return { label, url: null };
  } catch {
    return null;
  }
};

const renderLegalReferencePart = (referenceText: string): React.ReactNode => {
  if (!referenceText) return null;

  const jsonRef = parseLegalReferenceJsonString(referenceText);
  if (jsonRef?.url) {
    return (
      <a
        href={jsonRef.url}
        target="_blank"
        rel="noopener noreferrer"
        className="legal-reference-link"
      >
        {jsonRef.label}
      </a>
    );
  }
  // If the value is a JSON-encoded object but doesn't include a URL,
  // fall back to our heuristic URL builder using the parsed label (not raw JSON).
  const effectiveText = jsonRef ? jsonRef.label : referenceText;

  // Official URLs for Brazilian laws
  const lawUrls: Record<string, string> = {
    'lei_inquilinato': 'https://www.planalto.gov.br/ccivil_03/leis/l8245.htm',
    'cdc': 'https://www.planalto.gov.br/ccivil_03/leis/l8078.htm',
    'codigo_civil': 'https://www.planalto.gov.br/ccivil_03/leis/2002/l10406compilada.htm',
  };

  // Extract article number
  const articleMatch = effectiveText.match(/art\.?\s*(\d+)/i);
  const articleNum = articleMatch ? articleMatch[1] : null;

  // Identify law type
  let lawType: string | null = null;
  let baseUrl: string | null = null;

  if (effectiveText.match(/8\.?245|inquilinato|locação/i)) {
    lawType = 'lei_inquilinato';
    baseUrl = lawUrls.lei_inquilinato;
  } else if (effectiveText.match(/8\.?078|cdc|consumidor/i)) {
    lawType = 'cdc';
    baseUrl = lawUrls.cdc;
  } else if (effectiveText.match(/10\.?406|código civil|codigo civil/i)) {
    lawType = 'codigo_civil';
    baseUrl = lawUrls.codigo_civil;
  }

  // Build URL with anchor if article found
  let url = baseUrl;
  if (url && articleNum && lawType === 'lei_inquilinato') {
    url += `#art${articleNum}`;
  } else if (url && articleNum && lawType === 'cdc') {
    url += `#art${articleNum}`;
  }

  if (url) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="legal-reference-link"
      >
        {effectiveText}
      </a>
    );
  }

  return <span>{effectiveText}</span>;
};

// Helper function to parse and render legal references with clickable links
const renderLegalReference = (reference: unknown): React.ReactNode => {
  const parts = normalizeLegalReferenceParts(reference);
  if (parts.length === 0) return null;
  if (parts.length === 1) return renderLegalReferencePart(parts[0]);

  return (
    <span>
      {parts.map((part, index) => (
        <React.Fragment key={`${part}-${index}`}>
          {renderLegalReferencePart(part)}
          {index < parts.length - 1 ? '; ' : null}
        </React.Fragment>
      ))}
    </span>
  );
};

const PARTY_META_KEYS = new Set(['existe', 'exists', 'present', 'is_present', 'isPresent']);

const isMeaningfulString = (value: string): boolean => {
  const normalized = value.trim().toLowerCase();
  return Boolean(normalized) && normalized !== 'não especificado' && normalized !== 'não aplica';
};

const hasMeaningfulValue = (value: any): boolean => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return isMeaningfulString(value);
  if (typeof value === 'number') return true;
  if (typeof value === 'boolean') return true;
  if (Array.isArray(value)) return value.some(hasMeaningfulValue);
  if (typeof value === 'object') {
    return Object.entries(value).some(([key, inner]) => !PARTY_META_KEYS.has(key) && hasMeaningfulValue(inner));
  }
  return false;
};

const createFormatPartyFieldLabel = (t: (key: string) => string) => (key: string): string => {
  const labelMap: Record<string, string> = {
    nome: t('detail.parties.fields.name'),
    name: t('detail.parties.fields.name'),
    cpf: t('detail.parties.fields.cpf'),
    cpf_cnpj: 'CPF/CNPJ',
    cpfCnpj: 'CPF/CNPJ',
    cnpj: t('detail.parties.fields.cnpj'),
    rg: t('detail.parties.fields.rg'),
    orgao_emissor: t('detail.parties.fields.issuingBody'),
    orgaoEmissor: t('detail.parties.fields.issuingBody'),
    nacionalidade: t('detail.parties.fields.nationality'),
    nationality: t('detail.parties.fields.nationality'),
    estado_civil: t('detail.parties.fields.maritalStatus'),
    marital_status: t('detail.parties.fields.maritalStatus'),
    maritalStatus: t('detail.parties.fields.maritalStatus'),
    profissao: t('detail.parties.fields.profession'),
    profession: t('detail.parties.fields.profession'),
    occupation: t('detail.parties.fields.profession'),
    tipo: t('detail.parties.fields.type'),
    type: t('detail.parties.fields.type'),
    creci: 'CRECI',
    responsavel: t('detail.parties.fields.responsible'),
    responsible: t('detail.parties.fields.responsible'),
    telefone: t('detail.parties.fields.phone'),
    phone: t('detail.parties.fields.phone'),
    email: t('detail.parties.fields.email'),
    endereco: t('detail.parties.fields.address'),
    address: t('detail.parties.fields.address'),
    descricao: t('detail.parties.fields.description'),
    description: t('detail.parties.fields.description'),
    cidade: t('detail.parties.fields.city'),
    city: t('detail.parties.fields.city'),
    estado: 'UF',
    state: 'UF',
    cep: 'CEP',
    zip_code: 'CEP',
    zipCode: 'CEP',
  };
  return labelMap[key] || key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
};

const createGetPartyCardMeta = (t: (key: string) => string, formatPartyFieldLabel: (key: string) => string) => (key: string) => {
  const normalized = key.toLowerCase();

  if (['locatario', 'tenant'].includes(normalized)) {
    return { title: t('detail.parties.tenant'), icon: <UserIcon size={20} /> };
  }
  if (['locador', 'landlord'].includes(normalized)) {
    return { title: t('detail.parties.landlord'), icon: <HomeIcon size={20} /> };
  }
  if (['imobiliaria', 'agency', 'real_estate_agency', 'realestateagency'].includes(normalized)) {
    return { title: t('detail.parties.agency'), icon: <BuildingIcon size={20} /> };
  }
  if (['witnesses', 'testemunhas', 'witness'].includes(normalized)) {
    return { title: t('detail.parties.witness'), icon: <UserIcon size={20} /> };
  }
  if (['notary', 'cartorio', 'cartório'].includes(normalized)) {
    return { title: t('detail.parties.notary'), icon: <BuildingIcon size={20} /> };
  }
  if (['fiador', 'fiadores', 'guarantor', 'guarantors'].includes(normalized)) {
    return { title: t('detail.parties.guarantor'), icon: <ShieldIcon size={20} /> };
  }
  if (['imovel', 'property'].includes(normalized)) {
    return { title: t('detail.parties.property'), icon: <BuildingIcon size={20} /> };
  }

  return { title: formatPartyFieldLabel(key), icon: <UserIcon size={20} /> };
};

type PartyCardTranslations = {
  showDetails: string;
  hideDetails: string;
  detailsOf: string;
};

const createBuildPartyCards = (
  t: (key: string) => string,
  formatPartyFieldLabel: (key: string) => string,
  getPartyCardMeta: (key: string) => { title: string; icon: React.ReactNode },
  formatValue: (value: any) => string
) => (parties: any): React.ReactNode[] => {
  if (!parties || typeof parties !== 'object') return [];

  const translations: PartyCardTranslations = {
    showDetails: t('detail.parties.showDetails'),
    hideDetails: t('detail.parties.hideDetails'),
    detailsOf: t('detail.parties.title'),
  };
  const notSpecified = t('detail.values.notSpecified');

  const cards: React.ReactNode[] = [];
  const nameKeys = new Set([
    'nome',
    'name',
    'nome_completo',
    'nome completo',
    'full_name',
    'fullname',
    'razao_social',
    'razão_social',
    'razao social',
    'razão social',
  ]);

  const PartyCard: React.FC<{
    cardKey: string;
    title: string;
    icon: React.ReactNode;
    name: string;
    details: ReadonlyArray<readonly [string, string]>;
  }> = ({ cardKey, title, icon, name, details }) => {
    const [expanded, setExpanded] = React.useState(false);
    const detailsId = `${cardKey}-details`;

    return (
      <div className="party-card">
        <div className="party-card-header">
          <span className="party-avatar">{icon}</span>
          <h3>{title}</h3>
        </div>
        <div className="party-card-body">
          <div className="party-name">{name}</div>

          {details.length > 0 && (
            <>
              <button
                type="button"
                className="summary-expand-btn party-expand-btn"
                onClick={() => setExpanded((prev) => !prev)}
                aria-expanded={expanded}
                aria-controls={detailsId}
              >
                <span>{expanded ? translations.hideDetails : translations.showDetails}</span>
                {expanded ? <ChevronUpIcon size={20} /> : <ChevronDownIcon size={20} />}
              </button>

              {expanded && (
                <div id={detailsId} className="party-details" role="region" aria-label={`${title}`}>
                  {details.map(([fieldKey, value]) => (
                    <div key={`${cardKey}-${fieldKey}`} className="party-field">
                      <span className="party-label">{formatPartyFieldLabel(fieldKey)}</span>
                      <span className="party-value">{value}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  const addPartyCard = (partyKey: string, partyValue: any, index?: number, customTitle?: string) => {
    if (!partyValue || typeof partyValue !== 'object') return;

    if (!hasMeaningfulValue(partyValue)) return;

    const meta = getPartyCardMeta(partyKey);
    const preferredOrder = [
      'nome',
      'name',
      'cpf_cnpj',
      'cpfCnpj',
      'cpf',
      'cnpj',
      'creci',
      'tipo',
      'type',
      'endereco',
      'address',
      'telefone',
      'phone',
      'email',
      'responsavel',
      'profissao',
      'occupation',
      'estado_civil',
      'marital_status',
      'descricao',
      'description',
    ];

    const entries = Object.entries(partyValue)
      .filter(([k]) => !PARTY_META_KEYS.has(k) && k !== 'contact' && k !== 'contato')
      .map(([k, v]) => [k, formatValue(v)] as const)
      .filter(([, v]) => v && v !== notSpecified)
      .sort(([a], [b]) => {
        const ai = preferredOrder.indexOf(a);
        const bi = preferredOrder.indexOf(b);
        const aRank = ai === -1 ? 999 : ai;
        const bRank = bi === -1 ? 999 : bi;
        if (aRank !== bRank) return aRank - bRank;
        return a.localeCompare(b);
      });

    if (entries.length === 0) return;

    const key = index === undefined ? partyKey : `${partyKey}-${index}`;
    const title = customTitle || (index === undefined ? meta.title : `${meta.title} ${index + 1}`);
    const nameEntry = entries.find(([fieldKey]) => nameKeys.has(String(fieldKey).toLowerCase()));
    const name = nameEntry?.[1] || '';
    const details = entries.filter(([fieldKey]) => fieldKey !== nameEntry?.[0]);

    cards.push(
      <PartyCard
        key={key}
        cardKey={key}
        title={title}
        icon={meta.icon}
        name={name}
        details={details}
      />
    );
  };

  // Helper to compare two party objects for similarity
  const arePartiesSimilar = (party1: any, party2: any): boolean => {
    if (!party1 || !party2) return false;
    if (typeof party1 !== 'object' || typeof party2 !== 'object') return false;

    // Extract meaningful fields from both parties
    const getFields = (party: any) => {
      return Object.entries(party)
        .filter(([k]) => !PARTY_META_KEYS.has(k))
        .filter(([, v]) => {
          const formatted = formatValue(v);
          return formatted && formatted !== 'Não especificado';
        })
        .reduce((acc, [k, v]) => {
          acc[k.toLowerCase()] = String(formatValue(v)).toLowerCase().trim();
          return acc;
        }, {} as Record<string, string>);
    };

    const fields1 = getFields(party1);
    const fields2 = getFields(party2);

    // If either party has no meaningful fields, they're not similar
    if (Object.keys(fields1).length === 0 || Object.keys(fields2).length === 0) {
      return false;
    }

    // Check if key identifying fields match (name, cpf/cnpj)
    const nameKeys = ['nome', 'name'];
    const idKeys = ['cpf', 'cnpj', 'cpf_cnpj', 'cpfcnpj'];

    const getName = (fields: Record<string, string>) => {
      for (const key of nameKeys) {
        if (fields[key]) return fields[key];
      }
      return null;
    };

    const getId = (fields: Record<string, string>) => {
      for (const key of idKeys) {
        if (fields[key]) return fields[key];
      }
      return null;
    };

    const name1 = getName(fields1);
    const name2 = getName(fields2);
    const id1 = getId(fields1);
    const id2 = getId(fields2);

    // If both have names and they match, consider them similar
    if (name1 && name2 && name1 === name2) return true;

    // If both have IDs and they match, consider them similar
    if (id1 && id2 && id1 === id2) return true;

    return false;
  };

  const recognizedKeys = [
    'locador',
    'locatario',
    'fiadores',
    'fiador',
    'imobiliaria',
    'imovel',
    'landlord',
    'tenant',
    'guarantor',
    'guarantors',
    'agency',
    'property',
  ];

  const hasRecognized = recognizedKeys.some((k) => parties[k] !== undefined);
  if (hasRecognized) {
    const processedKeys = new Set<string>();

    const add = (k: string, v: any) => {
      if (processedKeys.has(k)) return;

      // Check if this is landlord/locador and if agency/imobiliaria exists and is similar
      if (['landlord', 'locador'].includes(k)) {
        const agencyKey = parties['agency'] !== undefined ? 'agency' :
                         parties['imobiliaria'] !== undefined ? 'imobiliaria' : null;

        if (agencyKey && arePartiesSimilar(v, parties[agencyKey])) {
          // Merge landlord and agency into one card
          const landlordMeta = getPartyCardMeta(k);
          const agencyMeta = getPartyCardMeta(agencyKey);
          const combinedTitle = `${agencyMeta.title} / ${landlordMeta.title}`;

          if (Array.isArray(v)) {
            v.forEach((item, idx) => addPartyCard(k, item, idx, `${combinedTitle} ${idx + 1}`));
          } else {
            addPartyCard(k, v, undefined, combinedTitle);
          }

          processedKeys.add(k);
          processedKeys.add(agencyKey);
          return;
        }
      }

      // Check if this is agency/imobiliaria and landlord was already processed
      if (['agency', 'imobiliaria'].includes(k) &&
          (processedKeys.has('landlord') || processedKeys.has('locador'))) {
        return; // Skip, already merged
      }

      // Normal processing
      if (Array.isArray(v)) {
        v.forEach((item, idx) => addPartyCard(k, item, idx));
      } else {
        addPartyCard(k, v);
      }
      processedKeys.add(k);
    };

    recognizedKeys.forEach((k) => {
      const v = (parties as any)[k];
      if (v === undefined) return;
      add(k, v);
    });

    return cards;
  }

  // Fallback: show any object/array inside parties as a card.
  Object.entries(parties).forEach(([k, v]) => {
    if (Array.isArray(v)) {
      v.forEach((item, idx) => addPartyCard(k, item, idx));
      return;
    }
    if (typeof v === 'object') addPartyCard(k, v);
  });

  return cards;
};

// Risk accordion item translations type
type RiskAccordionTranslations = {
  severityHigh: string;
  severityMedium: string;
  severityLow: string;
  upgradeAriaLabel: string;
  unlockTitle: string;
  unlockDescription: string;
  showLegalBasis: string;
  hideLegalBasis: string;
  whatYouCanDo: string;
  contractClause: string;
  whyItMatters: string;
  suggestedText: string;
  legalBasis: string;
  includeInMessage: string;
  financialImpactTag: string;
  defaultImpactSummary: string;
  // New fields
  whenItHappens: string;
  negotiationLevel: string;
  negotiationLevels: { alta: string; media: string; baixa: string };
  actionItems: string;
  deepAnalysis: string;
  deepLoading: string;
  deepMarketComparison: string;
  deepNegotiationStrength: string;
  deepWorstCase: string;
  marketStatus: { mais_rigida: string; padrao: string; favoravel: string };
  marketBaseline: string;
  thisContract: string;
  marketDeltaHigher: string;
  marketDeltaLower: string;
  marketDeltaEqual: string;
  worstCaseExampleLabel: string;
  addToNegotiation: string;
  addedToNegotiation: string;
  deepTeaser: string;
  deepClose: string;
  deepOpen: string;
  deepFullLabel: string;
  predictedOutcome: string;
  loadingMarket: string;
  loadingNegotiation: string;
  loadingWorst: string;
  confidence: string;
  negotiationStrengthAria: string;
  negotiationStrengthSublabel: string;
  legalFundamentos: string;
  clauseCount: string;
  legalBasisMeta: string;
  showScenarios: string;
  hideScenarios: string;
  sliderNavAria: string;
  sliderPrevAria: string;
  sliderNextAria: string;
  sliderPrev: string;
  sliderNext: string;
  understood: string;
  counter: string;
};

const formatComparisonValue = (
  value: number,
  comparison: { type?: string; unit?: string } | null | undefined,
): string => {
  if (!Number.isFinite(value)) return '';
  const unit = (comparison?.unit || '').trim();
  const type = comparison?.type;
  if (type === 'money') {
    const code = unit.toUpperCase() === 'USD' ? 'USD' : unit.toUpperCase() === 'EUR' ? 'EUR' : 'BRL';
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: code, maximumFractionDigits: 0 }).format(value);
    } catch {
      return `${value} ${unit}`.trim();
    }
  }
  if (type === 'percent') {
    return `${value}%`;
  }
  const rounded = Number.isInteger(value) ? value : Math.round(value * 100) / 100;
  return unit ? `${rounded} ${unit}` : String(rounded);
};

// ── RiskSlider Component ─────────────────────────────────────────────────────
const RiskSlider: React.FC<{
  items: Array<{ risk: any; originalIndex: number; isLocked: boolean }>;
  translations: RiskAccordionTranslations;
  selectedRisks: Set<number>;
  hasMessageBuilder: boolean;
  onToggle: (originalIndex: number, isLocked: boolean) => void;
  onSelectionToggle: (index: number) => void;
  documentId?: number;
}> = ({ items, translations, selectedRisks, hasMessageBuilder, onToggle, onSelectionToggle, documentId }) => {
  const [current, setCurrent] = React.useState(0);
  if (items.length === 0) return null;

  const scrollToRiskSectionTop = () => {
    window.requestAnimationFrame(() => {
      window.document
        .getElementById('section-risks-overview')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const prev = () => {
    setCurrent(c => (c - 1 + items.length) % items.length);
    scrollToRiskSectionTop();
  };
  const next = () => {
    setCurrent(c => (c + 1) % items.length);
    scrollToRiskSectionTop();
  };
  const { risk, originalIndex, isLocked } = items[current];

  return (
    <div className="risk-slider">
      <div className="risk-slider-card">
        <RiskAccordionItem
          key={`${originalIndex}-${current}`}
          risk={risk}
          recommendation={risk.recommendation}
          index={originalIndex}
          riskIndex={originalIndex}
          isExpanded={false}
          onToggle={() => onToggle(originalIndex, isLocked)}
          locked={isLocked}
          isSelected={selectedRisks.has(originalIndex)}
          onSelectionToggle={onSelectionToggle}
          hasMessageBuilder={hasMessageBuilder}
          translations={translations}
          documentId={documentId}
        />
      </div>
      {items.length > 1 && (
        <nav className="slider-nav-pill" aria-label={translations.sliderNavAria}>
          <button className="slider-nav-btn" onClick={prev} aria-label={translations.sliderPrevAria}>
            {translations.sliderPrev}
          </button>
          <span className="slider-nav-count" aria-live="polite" aria-atomic="true">
            {current + 1} / {items.length}
          </span>
          <button className="slider-nav-btn" onClick={next} aria-label={translations.sliderNextAria}>
            {translations.sliderNext}
          </button>
        </nav>
      )}
    </div>
  );
};

// Helper Component: Risk Card — layered case file with 6 zones
const RiskAccordionItem: React.FC<{
  risk: any;
  recommendation: any;
  index: number;
  riskIndex?: number;
  isExpanded: boolean;
  onToggle: () => void;
  locked?: boolean;
  isSelected?: boolean;
  onSelectionToggle?: (index: number) => void;
  hasMessageBuilder?: boolean;
  translations: RiskAccordionTranslations;
  documentId?: number;
}> = ({ risk, recommendation, index, riskIndex, isExpanded, onToggle, locked = false, isSelected = false, onSelectionToggle, hasMessageBuilder = false, translations, documentId }) => {
  const [showDetails, setShowDetails] = React.useState(false);
  const [understood, setUnderstood] = React.useState(false);
  const [deepAnalysis, setDeepAnalysis] = React.useState<any>(null);
  const [deepLoading, setDeepLoading] = React.useState(false);
  const [showDeep, setShowDeep] = React.useState(false);
  const [activeDeepTab, setActiveDeepTab] = React.useState<'market' | 'negotiation' | 'worst'>('market');
  const { t } = useTranslation('documents');

  const getSeverityConfig = (severity: string) => {
    switch (severity) {
      case 'high':
        return { label: translations.severityHigh, color: '#dc2626' };
      case 'medium':
        return { label: translations.severityMedium, color: '#f59e0b' };
      default:
        return { label: translations.severityLow, color: '#0c8b5c' };
    }
  };

  const config = getSeverityConfig(risk.severity);

  const normalizedTitle = (value?: string) => value?.replace(/\s+/g, ' ').trim() || '';

  const getProblemTitle = () => {
    const title = normalizedTitle(risk.title);
    return title
      .replace(/^(você pode|voce pode|vale|recomenda-se|sugerimos|é importante|é recomendável)\s+/i, '')
      .replace(/^(negociar|solicitar|pedir|verificar|confirmar|revisar|ajustar)\s+/i, '')
      .trim() || title;
  };

  const displayTitle = getProblemTitle();

  const legalReference = risk.law_reference || '';
  const legalBasis = risk.legal_basis || risk.legalBasis || '';

  const recommendationText = (() => {
    if (!recommendation) return '';
    if (typeof recommendation === 'string') return recommendation;
    return recommendation.summary || recommendation.suggestion || recommendation.description || recommendation.action || '';
  })();

  const suggestedText = typeof recommendation === 'object' ? recommendation?.suggested_text : null;
  const clausePage = risk.clause_page ?? risk.clausePage ?? risk.page_number ?? risk.pageNumber ?? risk.page;

  const getImpactSummary = () => {
    const impact = normalizedTitle(risk.potential_impact || '');
    const explanation = normalizedTitle(risk.explanation || '');
    const titleLower = displayTitle.toLowerCase();
    const impactLower = impact.toLowerCase();
    if (impactLower && !impactLower.includes(titleLower)) return impact;
    if (explanation && !explanation.toLowerCase().includes(titleLower)) {
      const match = /^[^.!?]+[.!?]/.exec(explanation);
      return match ? match[0].trim() : explanation.substring(0, 80) + '...';
    }
    return impact || translations.defaultImpactSummary;
  };

  const impactSummary = getImpactSummary();
  const normalizedImpactSummary = normalizedTitle(impactSummary).toLowerCase();
  const normalizedExplanationText = normalizedTitle(risk.explanation || '').toLowerCase();
  const shouldShowExplanationInLegal =
    Boolean(risk.explanation) &&
    Boolean(normalizedExplanationText) &&
    Boolean(normalizedImpactSummary) &&
    normalizedExplanationText !== normalizedImpactSummary &&
    !normalizedExplanationText.startsWith(normalizedImpactSummary) &&
    !normalizedImpactSummary.startsWith(normalizedExplanationText);

  const getUniqueClauseText = () => {
    if (!risk.clause_text) return '';
    return normalizedTitle(risk.clause_text);
  };

  const whenItHappens: string[] = Array.isArray(risk.when_it_happens) ? risk.when_it_happens : [];
  const actionItems: string[] = Array.isArray(risk.action_items) ? risk.action_items : [];
  const negotiationLevel: string = risk.negotiation_level || 'media';

  const negotiationLabel = translations.negotiationLevels?.[negotiationLevel as keyof typeof translations.negotiationLevels] || negotiationLevel;

  const hasLegalZone = !!(getUniqueClauseText() || risk.explanation || legalBasis || legalReference || suggestedText || recommendationText);

  const handleDeepAnalysis = async () => {
    if (deepAnalysis) { setShowDeep(v => !v); return; }
    if (documentId == null || riskIndex == null) return;
    setShowDeep(true);
    setDeepLoading(true);
    try {
      const res = await api.post(`/documents/${documentId}/risks/${riskIndex}/deep-analysis`);
      setDeepAnalysis(res.data);
    } catch {
      // keep showDeep open so the user sees the empty state rather than a silent failure
    } finally {
      setDeepLoading(false);
    }
  };

  const marketStatusLabel = deepAnalysis?.market_comparison?.status
    ? (translations.marketStatus?.[deepAnalysis.market_comparison.status as keyof typeof translations.marketStatus] || deepAnalysis.market_comparison.status)
    : '';

  const isPremiumForDeep = documentId != null && riskIndex != null;

  // Scenarios for the "Ver quando ocorre" disclosure (legal moves into deep panel)
  const hasScenarios = whenItHappens.length > 0;

  return (
    <div
      id={`risk-${index}`}
      className={`risk-tab risk-tab--${risk.severity}${locked && risk.severity !== 'high' ? ' has-locked-overlay' : ''}`}
    >
      {/* Locked overlay */}
      {locked && risk.severity !== 'high' && (
        <div className="locked-content-overlay" onClick={onToggle}>
          <div className="locked-content-message">
            <LockIcon size={32} />
            <h4>{t('detail.risks.locked.upgradeHeader')}</h4>
            <ul className="locked-content-list">
              {(t('detail.risks.locked.upgradeItems', { returnObjects: true }) as unknown as string[]).map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <div className="risk-tab-inner">
        {/* Header: title + ghost "Marcar como visto" button */}
        <div className="risk-tab-head">
          <h3 className="risk-tab-title">{displayTitle}</h3>
          {!locked && (
            <button
              type="button"
              className={`risk-tab-understood${understood ? ' is-checked' : ''}`}
              onClick={() => setUnderstood(v => !v)}
              aria-pressed={understood}
            >
              <CheckIcon size={12} />
              <span>{translations.understood}</span>
            </button>
          )}
        </div>

        {/* Severity + confidence meta line */}
        <div className="risk-tab-severity-line">
          <span className={`risk-sev-dot risk-sev-dot--${risk.severity}`}>●</span>
          <span className="risk-sev-label">{config.label}</span>
          {typeof risk.financial_impact_brl === 'number' && risk.financial_impact_brl > 0 && (
            <>
              <span className="risk-sev-sep">·</span>
              <span className="risk-sev-financial">
                {translations.financialImpactTag.replace(
                  '{{amount}}',
                  new Intl.NumberFormat(undefined, { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(risk.financial_impact_brl)
                )}
              </span>
            </>
          )}
          <span className="risk-sev-sep">·</span>
          <span className="risk-sev-neg">{translations.negotiationLevel} {negotiationLabel}</span>
          {/* 6a — confidence signal (rendered when model provides it) */}
          {risk.confidence_level && (
            <>
              <span className="risk-sev-sep">·</span>
              <span className="risk-confidence-chip">
                {(['alta', 'media', 'baixa'] as const).map((_, i) => (
                  <span
                    key={i}
                    className={`risk-conf-dot${i < (['alta', 'media', 'baixa'].indexOf(risk.confidence_level) + 1) ? ' is-filled' : ''}`}
                  />
                ))}
                <span className="risk-confidence-label">
                  {translations.confidence.replace('{{level}}', String(risk.confidence_level))}
                </span>
              </span>
            </>
          )}
        </div>

        {/* Impact — always visible */}
        <p className="risk-tab-impact">{impactSummary}</p>

        {/* 6b — "Por que isso importa" — conversational AI voice (optional field) */}
        {risk.why_it_matters && (
          <p className="risk-tab-why-it-matters">{risk.why_it_matters}</p>
        )}

        {/* ── Layer 2: O que você pode fazer — always visible, never enters the tabs ── */}
        {!locked && (actionItems.length > 0 || recommendationText) && (
          <div className="risk-tab-actions">
            <p className="risk-tab-actions-heading">{translations.actionItems}</p>
            <ul className="risk-tab-actions-list">
              {(actionItems.length > 0 ? actionItems : [recommendationText].filter(Boolean) as string[]).map((item, i) => (
                <li key={i} className="risk-action-row">
                  <span className="risk-action-text">{item}</span>
                </li>
              ))}
            </ul>
            {hasMessageBuilder && (
              <button
                type="button"
                className="risk-add-all-btn"
                onClick={() => onSelectionToggle?.(index)}
              >
                {isSelected ? translations.addedToNegotiation : translations.addToNegotiation}
              </button>
            )}
          </div>
        )}

        {/* ── Layer 3 trigger — teaser hidden once panel is open ── */}
        {isPremiumForDeep && !locked && (
          <div className="risk-deep-promo">
            {!showDeep && (
              <p className="risk-deep-promo-teaser">
                {translations.deepTeaser}
              </p>
            )}
            <button
              type="button"
              className="risk-deep-promo-btn"
              onClick={handleDeepAnalysis}
            >
              {showDeep ? translations.deepClose : translations.deepOpen}
            </button>
          </div>
        )}

        {/* ── Layer 3: Analysis tabs — tab-unique content only, no legal inside ── */}
        {isPremiumForDeep && !locked && showDeep && (
          <div className="risk-deep-section">
            {deepLoading && (
              <div className="risk-deep-loading">{translations.deepLoading}</div>
            )}

            {!deepLoading && (
              <>
                {/* 6e — ✦ AI badge above the tab strip */}
                <div className="deep-ai-header">
                  <span className="deep-ai-badge">✦</span>
                  <span className="deep-ai-label">{translations.deepFullLabel}</span>
                </div>

                {/* Tab strip */}
                <div className="deep-tabs" role="tablist">
                  {(['market', 'negotiation', 'worst'] as const).map((tab) => {
                    const labels = {
                      market: translations.deepMarketComparison,
                      negotiation: translations.deepNegotiationStrength,
                      worst: translations.deepWorstCase,
                    };
                    return (
                      <button
                        key={tab}
                        role="tab"
                        type="button"
                        aria-selected={activeDeepTab === tab}
                        className={`deep-tab${activeDeepTab === tab ? ' is-active' : ''}`}
                        onClick={() => setActiveDeepTab(tab)}
                      >
                        {labels[tab]}
                      </button>
                    );
                  })}
                </div>

                {/* ── Tab 1: Mercado — status pill · adaptive comparison · explanation ── */}
                {activeDeepTab === 'market' && (
                  <div className="deep-panel" role="tabpanel">
                    {deepAnalysis ? (
                      <>
                        <div className={`deep-metric-pill deep-metric-pill--${
                          deepAnalysis.market_comparison?.status === 'mais_rigida' ? 'red'
                          : deepAnalysis.market_comparison?.status === 'favoravel' ? 'green'
                          : 'amber'
                        }`}>
                          {marketStatusLabel}
                        </div>

                        {(() => {
                          const status = deepAnalysis.market_comparison?.status ?? 'padrao';
                          const comparison = deepAnalysis.market_comparison?.comparison;
                          const market = Number(comparison?.market_value);
                          const contract = Number(comparison?.contract_value);
                          const hasNumeric = comparison
                            && Number.isFinite(market)
                            && Number.isFinite(contract)
                            && (market > 0 || contract > 0);

                          if (!hasNumeric) return null;

                          const max = Math.max(market, contract);
                          const marketPct = max > 0 ? (market / max) * 100 : 0;
                          const contractPct = max > 0 ? (contract / max) * 100 : 0;

                          // Severity color from status (red/amber/green)
                          const severityColor = status === 'mais_rigida' ? 'var(--risk-crit, #E11D48)'
                            : status === 'favoravel' ? 'var(--risk-low, #047857)'
                            : 'var(--risk-mod, #B45309)';

                          // Delta % between contract and market, signed relative to market baseline
                          const deltaPct = market > 0 ? Math.round(((contract - market) / market) * 100) : null;
                          const direction = comparison.direction === 'lower_is_worse' ? 'lower_is_worse' : 'higher_is_worse';
                          const contractIsWorse = direction === 'higher_is_worse' ? contract > market : contract < market;
                          const deltaTone = deltaPct == null || deltaPct === 0
                            ? 'neutral'
                            : (contractIsWorse ? 'bad' : 'good');

                          const marketLabel = comparison.market_label || formatComparisonValue(market, comparison);
                          const contractLabel = comparison.contract_label || formatComparisonValue(contract, comparison);

                          return (
                            <div className="deep-market-bars">
                              <div className="deep-market-row">
                                <span className="deep-market-row-label">{translations.marketBaseline}</span>
                                <div className="deep-bar-track">
                                  <div className="deep-bar-fill deep-bar-fill--market" style={{ width: `${marketPct}%` }} />
                                </div>
                                <span className="deep-market-row-value">{marketLabel}</span>
                              </div>
                              <div className="deep-market-row">
                                <span className="deep-market-row-label">{translations.thisContract}</span>
                                <div className="deep-bar-track">
                                  <div className="deep-bar-fill" style={{ width: `${contractPct}%`, background: severityColor }} />
                                </div>
                                <span className="deep-market-row-value">{contractLabel}</span>
                              </div>
                              {deltaPct != null && (
                                <div className={`deep-market-delta deep-market-delta--${deltaTone}`}>
                                  {deltaPct === 0
                                    ? translations.marketDeltaEqual
                                    : (deltaPct > 0
                                        ? translations.marketDeltaHigher.replace('{{pct}}', String(Math.abs(deltaPct)))
                                        : translations.marketDeltaLower.replace('{{pct}}', String(Math.abs(deltaPct))))}
                                </div>
                              )}
                            </div>
                          );
                        })()}

                        {deepAnalysis.market_comparison?.explanation && (
                          <p className="deep-panel-text">{deepAnalysis.market_comparison.explanation}</p>
                        )}
                        {/* 6c — Predicted outcome (optional model field) */}
                        {deepAnalysis.predicted_outcome && (
                          <p className="deep-panel-prediction">
                            <span className="deep-prediction-label">{translations.predictedOutcome}</span>{' '}
                            {deepAnalysis.predicted_outcome}
                          </p>
                        )}
                      </>
                    ) : (
                      <p className="deep-panel-empty">{translations.loadingMarket}</p>
                    )}
                  </div>
                )}

                {/* ── Tab 2: Negociação — strength pill · dot meter · one sentence ── */}
                {activeDeepTab === 'negotiation' && (
                  <div className="deep-panel" role="tabpanel">
                    {deepAnalysis ? (
                      <>
                        {deepAnalysis.negotiation_strength?.level && (() => {
                          const lvl = deepAnalysis.negotiation_strength.level as string;
                          const meterFill = ({ alta: 8, media: 5, baixa: 2 } as Record<string, number>)[lvl] ?? 5;
                          const label = translations.negotiationLevels?.[lvl as keyof typeof translations.negotiationLevels] ?? lvl;
                          return (
                            <>
                              <div className={`deep-strength deep-strength--${lvl}`}>
                                <div className="deep-metric-pill deep-metric-pill--inherit">
                                  {label}
                                </div>
                                <div
                                  className="deep-strength-meter"
                                  role="meter"
                                  aria-valuenow={meterFill}
                                  aria-valuemin={0}
                                  aria-valuemax={10}
                                  aria-label={translations.negotiationStrengthAria.replace('{{level}}', label)}
                                >
                                  {Array.from({ length: 10 }, (_, i) => (
                                    <span key={i} className={`deep-meter-dot${i < meterFill ? ' is-filled' : ''}`} />
                                  ))}
                                </div>
                                <span className="deep-strength-sublabel">{translations.negotiationStrengthSublabel}</span>
                              </div>
                            </>
                          );
                        })()}

                        {/* One paragraph only — no COMO NEGOCIAR list (those live in Layer 2 above) */}
                        {deepAnalysis.negotiation_strength?.reasoning && (
                          <p className="deep-panel-text">{deepAnalysis.negotiation_strength.reasoning}</p>
                        )}
                      </>
                    ) : (
                      <p className="deep-panel-empty">{translations.loadingNegotiation}</p>
                    )}
                  </div>
                )}

                {/* ── Tab 3: Pior caso — amber pill · explanation · real-world example ── */}
                {activeDeepTab === 'worst' && (
                  <div className="deep-panel" role="tabpanel">
                    {deepAnalysis?.worst_case ? (
                      <>
                        <div className="deep-metric-pill deep-metric-pill--amber">
                          {translations.deepWorstCase}
                        </div>
                        <p className="deep-panel-text">{deepAnalysis.worst_case}</p>
                        {deepAnalysis.worst_case_example && (
                          <div className="deep-example">
                            <span className="deep-example-label">{translations.worstCaseExampleLabel}</span>
                            <p className="deep-example-text">{deepAnalysis.worst_case_example}</p>
                          </div>
                        )}
                      </>
                    ) : (
                      <p className="deep-panel-empty">{translations.loadingWorst}</p>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ── Fundamentos Jurídicos — single collapsible, renders exactly once ── */}
        {hasLegalZone && (
          <details className="risk-fundamentos">
            <summary className="risk-fundamentos-summary">
              <span>{translations.legalFundamentos}</span>
              {clausePage && (
                <span className="risk-fundamentos-meta">
                  {getUniqueClauseText() ? translations.clauseCount : ''}
                  {(legalBasis || legalReference) ? ` · ${translations.legalBasisMeta}` : ''}
                </span>
              )}
              <ChevronDownIcon size={13} className="risk-fundamentos-chevron" />
            </summary>
            <div className="risk-fundamentos-body">
              {getUniqueClauseText() && (
                <div className="risk-tab-detail-group">
                  <p className="risk-tab-detail-label">{translations.contractClause}</p>
                  <div className="risk-tab-clause">"{getUniqueClauseText()}"</div>
                  {clausePage && (
                    <div className="risk-clause-meta">
                      {t('documents:detail.risks.card.clausePage', { page: clausePage })}
                    </div>
                  )}
                </div>
              )}
              {shouldShowExplanationInLegal && (
                <div className="risk-tab-detail-group">
                  <p className="risk-tab-detail-label">{translations.whyItMatters}</p>
                  <p className="risk-tab-detail-text">{risk.explanation}</p>
                </div>
              )}
              {suggestedText && (
                <div className="risk-tab-detail-group">
                  <p className="risk-tab-detail-label">{translations.suggestedText}</p>
                  <div className="risk-tab-clause">"{suggestedText}"</div>
                </div>
              )}
              {(legalBasis || legalReference) && (
                <div className="risk-tab-detail-group">
                  <p className="risk-tab-detail-label">{translations.legalBasis}</p>
                  {legalBasis && <p className="risk-tab-detail-text">{legalBasis}</p>}
                  {legalReference && <p className="risk-tab-detail-text">{renderLegalReference(legalReference)}</p>}
                </div>
              )}
            </div>
          </details>
        )}

        {/* Footer: scenarios only */}
        {hasScenarios && (
          <div className="risk-tab-footer">
            <button
              type="button"
              className="risk-tab-show-details"
              onClick={() => locked ? onToggle() : setShowDetails(v => !v)}
            >
              {locked && <LockIcon size={12} />}
              <span>{showDetails ? translations.hideScenarios : translations.showScenarios}</span>
              {!locked && (showDetails ? <ChevronUpIcon size={13} /> : <ChevronDownIcon size={13} />)}
            </button>
          </div>
        )}

        {/* Scenarios expanded */}
        {!locked && showDetails && whenItHappens.length > 0 && (
          <div className="risk-tab-details">
            <div className="risk-tab-detail-group">
              <p className="risk-tab-detail-label">{translations.whenItHappens}</p>
              <ul className="risk-tab-detail-list">
                {whenItHappens.map((item, i) => <li key={i}>{item}</li>)}
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Helper function to render summary cards with tiered structure
const createRenderSummaryCards = (formatValue: (value: any) => string, notSpecified: string) => (summary: any, tier: 'tier1' | 'tier2' | 'all' = 'all'): React.ReactNode[] => {
  const cards: React.ReactNode[] = [];

  const buildDisplaySummary = (data: any) => {
    const enriched = { ...(data || {}) };
    const hasStart = enriched.data_inicio && enriched.data_inicio !== notSpecified;
    const hasEnd = enriched.data_termino && enriched.data_termino !== notSpecified;
    const hasPrazo = enriched.prazo_locacao && enriched.prazo_locacao !== notSpecified;
    if (hasPrazo || hasStart || hasEnd) {
      enriched.prazo_locacao = {
        prazo: hasPrazo ? enriched.prazo_locacao : undefined,
        data_inicio: hasStart ? enriched.data_inicio : undefined,
        data_termino: hasEnd ? enriched.data_termino : undefined,
      };
    }
    delete enriched.data_inicio;
    delete enriched.data_termino;
    return enriched;
  };

  const displaySummary = buildDisplaySummary(summary);

  // Tier 1: Always visible - exactly 4 cards (highest priority)
  const tier1Fields = [
    'valor_aluguel',      // 💰 Valor do Aluguel
    'prazo_locacao',      // 📆 Prazo de Locação
    'multas',             // ⚠️ Multa Rescisória
    'reajuste',           // 📈 Reajuste do Aluguel
  ];

  // Tier 2: Expandable - all other fields
  const tier2Fields = [
    'pagamento',
    'condominio',
    'reparos',
    'iptu',
    'seguro',
    'vistoria',
    'pintura',
    'uso_imovel',
    'renovacao',
    'notificacao',
    'encargos_extras',
    'garantia',
    'fiador',
    'animais_permitidos',
  ];

  const renderCard = (key: string, value: any) => {
    if (!value || value === notSpecified) return;

    const isObjectValue = typeof value === 'object' && !Array.isArray(value);
    const hasValidChildren =
      isObjectValue &&
      Object.values(value).some((child) => child && child !== notSpecified);

    // Simple value card
    if (!isObjectValue) {
      cards.push(
        <SummaryCard
          key={key}
          icon={getIconForField(key)}
          label={formatFieldLabel(key)}
          value={formatValue(value)}
        />
      );
      return;
    }

    // Structured object card
    if (hasValidChildren) {
      const displayValue = (
        <div className="summary-card-details">
          {Object.entries(value)
            .filter(([, v]) => v && v !== notSpecified)
            .map(([childKey, childVal]) => (
              <div key={childKey} className="summary-detail-item">
                <span className="summary-detail-label">{formatFieldLabel(childKey)}</span>
                <span className="summary-detail-value">
                  <TruncatedText text={formatValue(childVal)} maxLength={100} />
                </span>
              </div>
            ))}
        </div>
      );

      cards.push(
        <SummaryCard
          key={key}
          icon={getIconForField(key)}
          label={formatFieldLabel(key)}
          value={displayValue}
        />
      );
    }
  };

  // Determine which fields to render based on tier
  if (tier === 'tier1' || tier === 'all') {
    tier1Fields.forEach((field) => renderCard(field, displaySummary[field]));
  }

  if (tier === 'tier2' || tier === 'all') {
    // Render tier 2 ordered fields
    tier2Fields.forEach((field) => renderCard(field, displaySummary[field]));

    // Render any remaining fields not in tier1 or tier2
    Object.entries(displaySummary).forEach(([key, value]) => {
      if (!tier1Fields.includes(key) && !tier2Fields.includes(key)) {
        renderCard(key, value);
      }
    });
  }

  return cards;
};

const getIconForField = (field: string): React.ReactNode => {
  const iconMap: { [key: string]: React.ReactNode } = {
    valor_aluguel: <DollarSignIcon size={20} />,
    prazo_locacao: <CalendarIcon size={20} />,
    data_inicio: <CalendarIcon size={20} />,
    data_termino: <CalendarIcon size={20} />,
    reajuste: <TrendingUpIcon size={20} />,
    garantia: <ShieldIcon size={20} />,
    fiador: <UserIcon size={20} />,
    multas: <AlertIcon size={20} />,
    pagamento: <CreditCardIcon size={20} />,
    condominio: <BuildingIcon size={20} />,
    animais_permitidos: <PawPrintIcon size={20} />,
    pintura: <PaintbrushIcon size={20} />,
    reparos: <WrenchIcon size={20} />,
    vistoria: <SearchIcon size={20} />,
    uso_imovel: <HomeIcon size={20} />,
    seguro: <ShieldIcon size={20} />,
    iptu: <BuildingIcon size={20} />,
    encargos_extras: <DollarSignIcon size={20} />,
    renovacao: <RefreshCwIcon size={20} />,
    notificacao: <MailIcon size={20} />,
  };
  return iconMap[field] || <ClipboardIcon size={20} />;
};

const formatFieldLabel = (key: string): string => {
  const tDocs = i18n.t.bind(i18n) as (key: string, options?: any) => string;
  const fallback = key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  const translated = tDocs(`documents:detail.summary.fields.${key}`, { defaultValue: fallback });
  return translated || fallback;
};

export default DocumentDetailNew;
